import { EnterForm } from '../components/EnterForm';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFY, isDateInFY, getFYDateError } from '../context/FYContext';
import { INDIAN_STATES } from './GST';
import { usePageCallbacks } from '../hooks/usePageCallbacks';
import {
  getSupplierGroups, createSupplierGroup, updateSupplierGroup, deleteSupplierGroup,
  getSuppliers, createSupplier, updateSupplier, deleteSupplier,
  getPurchaseOrders, getLastPurchaseOrder, createPurchaseOrder, updatePurchaseOrder, deletePurchaseOrder,
  getPurchaseEntries, createPurchaseEntry, updatePurchaseEntry, deletePurchaseEntry,
  bulkDeletePurchaseOrders, bulkDeletePurchaseEntries, bulkDeleteSuppliers,
  getPurchaseOutstanding, getPurchasePaymentOutstanding,
  getPurchasePayments, createPurchasePayment, clearPurchasePayment, bouncePurchasePayment, deletePurchasePayment,
  getProducts, createProduct, updateProduct
} from '../lib/api';
import api, { getHSNCodes, getCompanySettings, getHSNRateForDate } from '../lib/api';
import { formatCurrency, formatDate, getToday, getDateRange, formatApiErrorDetail } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { SectionTiles } from '../components/SectionTiles';
import { Plus, Package, Truck, AlertCircle, Pencil, Trash2, ChevronDown, ChevronUp, Building2, ClipboardList, IndianRupee, FileCheck, BookOpen, FileText, Printer } from 'lucide-react';
import { ExportToolbar } from '../components/ui/export-toolbar';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { SearchableSelect } from '../components/ui/searchable-select';
import { DateInput } from '../components/ui/date-input';
import { PhoneInput } from '../components/ui/phone-input';
import { DateRangeFilter } from '../components/ui/date-range-filter';
import { useSortableTable, SortableHeader } from '../components/ui/sortable-table';
import { FormShortcutHints } from '../components/ui/form-shortcut-hints';
import { toast } from 'sonner';

const emptyContact = { name: '', department: '', mobile_number: '', email: '' };
const emptyLineItem = { product_id: '', description: '', hsn_code: '', quantity_kg: '', quantity_bags: '', rate: '', brand: '', gst_rate: '', cgst_amount: '', sgst_amount: '', igst_amount: '', tax_type: '' };
const toTons = (kg) => kg ? `${(kg / 1000).toFixed(2)} Tons` : '0 Tons';

const Purchase = () => {
  const { isAdmin } = useAuth();
  const { selectedFY, fyStartDate, fyEndDate, shortLabel: fyLabel } = useFY();
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = location.state?.tab || '';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Listen for quick-open tab switch events
  useEffect(() => {
    const handleQuickOpenTab = (e) => {
      const tabMap = { entries: 'purchase-entries', suppliers: 'suppliers', outstanding: 'outstanding', orders: 'purchase-orders' };
      const tab = tabMap[e.detail?.tab] || e.detail?.tab;
      if (tab && TABS.includes(tab)) setActiveTab(tab);
    };
    window.addEventListener('quickopen-tab', handleQuickOpenTab);
    return () => window.removeEventListener('quickopen-tab', handleQuickOpenTab);
  }, []); // eslint-disable-line

  const TABS = ['purchase-orders', 'purchase-entries', 'outstanding', 'payment-outstanding', 'payments', 'suppliers'];
  const SECTION_DEFS = [
    { key: '1', value: 'purchase-orders', label: 'Purchase Orders', icon: Package },
    { key: '2', value: 'purchase-entries', label: 'Purchase', icon: Truck },
    { key: '3', value: 'outstanding', label: 'PO Outstanding', icon: ClipboardList },
    { key: '4', value: 'payment-outstanding', label: 'Purchase Outstanding', icon: IndianRupee },
    { key: '5', value: 'payments', label: 'Payments', icon: FileCheck },
    { key: '6', value: 'supplier-ledger', label: 'Supplier Ledger', icon: BookOpen },
    { key: '7', value: 'suppliers', label: 'Suppliers', icon: Building2 },
    { key: '8', value: 'notes', label: 'Debit Notes', icon: FileText },
  ];
  const switchTab = (direction) => {
    setActiveTab(t => {
      if (!t) return TABS[0];
      const i = TABS.indexOf(t);
      return direction === 'next'
        ? TABS[(i + 1) % TABS.length]
        : TABS[(i - 1 + TABS.length) % TABS.length];
    });
  };

  // Shared data
  const [supplierGroups, setSupplierGroups] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // PO state
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [allPurchaseOrders, setAllPurchaseOrders] = useState([]);
  const [poDialogOpen, setPoDialogOpen] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [poForm, setPoForm] = useState({ supplier_group_id: '', date: getToday(), line_items: [{ ...emptyLineItem }], remarks: '', round_off: '', credit_days: '' });
  const [poError, setPoError] = useState('');
  const [poSubmitting, setPoSubmitting] = useState(false);
  const [poEditRoundOff, setPoEditRoundOff] = useState(false);
  const currentMonth = getDateRange('current-month');
  const [poDateRange, setPoDateRange] = useState({ start_date: currentMonth.start_date, end_date: currentMonth.end_date });
  const [poQuick, setPoQuick] = useState('current-month');

  // Purchase Entry state
  const [purchaseEntries, setPurchaseEntries] = useState([]);
  const [peDialogOpen, setPeDialogOpen] = useState(false);
  const [peForm, setPeForm] = useState({ purchase_order_id: '', supplier_id: '', date: getToday(), line_items: [{ ...emptyLineItem }], invoice_number: '', vehicle_number: '', remarks: '', round_off: '', credit_days: '' });
  const [peError, setPeError] = useState('');
  const [peSubmitting, setPeSubmitting] = useState(false);
  const [peEditRoundOff, setPeEditRoundOff] = useState(false);
  const [peDateRange, setPeDateRange] = useState({ start_date: currentMonth.start_date, end_date: currentMonth.end_date });
  const [peQuick, setPeQuick] = useState('current-month');
  const [selectedPOForEntry, setSelectedPOForEntry] = useState(null);

  // Outstanding state
  const [outstanding, setOutstanding] = useState(null);
  const [outstandingLoading, setOutstandingLoading] = useState(false);

  // Payment outstanding state
  const [paymentOutstanding, setPaymentOutstanding] = useState(null);
  const [paymentOutstandingLoading, setPaymentOutstandingLoading] = useState(false);

  // Unified payment state
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [selectedPayIds, setSelectedPayIds] = useState([]);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payForm, setPayForm] = useState({ supplier_id: '', amount: '', date: getToday(), mode: 'online', reference: '', bank_name: '', remarks: '', allocations: [] });
  const [paymentDateRange, setPaymentDateRange] = useState({ start_date: currentMonth.start_date, end_date: currentMonth.end_date });
  const [paymentQuick, setPaymentQuick] = useState('current-month');
  const [payError, setPayError] = useState('');
  const [paySubmitting, setPaySubmitting] = useState(false);

  // Clear cheque dialog state
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearingPayment, setClearingPayment] = useState(null);
  const [clearDate, setClearDate] = useState(getToday());
  const [clearSubmitting, setClearSubmitting] = useState(false);

  // Selection state
  const [selectedPoIds, setSelectedPoIds] = useState([]);
  const [selectedPeIds, setSelectedPeIds] = useState([]);
  const [selectedFirmIds, setSelectedFirmIds] = useState([]);

  // Supplier state
  const [allFirms, setAllFirms] = useState([]);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupCreditDays, setEditGroupCreditDays] = useState('');

  const [firmDialogOpen, setFirmDialogOpen] = useState(false);
  const [editingFirm, setEditingFirm] = useState(null);
  const [firmForm, setFirmForm] = useState({ group_id: '', company_name: '', company_email: '', contact_persons: [{ ...emptyContact }], credit_days: '30', gstin: '', state: '', state_code: '' });
  const [firmError, setFirmError] = useState('');
  const [firmSubmitting, setFirmSubmitting] = useState(false);

  // Quick product creation state
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productForm, setProductForm] = useState({ name: '', category: '', group: '', kg_per_bag: '', brands: '', hsn_code: '', default_gst_rate: '', description: '' });
  const [productSubmitting, setProductSubmitting] = useState(false);
  const [productError, setProductError] = useState('');

  // GST data
  const [hsnCodes, setHsnCodes] = useState([]);
  const [companySettings, setCompanySettings] = useState({ state: '', state_code: '' });

  // Quick supplier creation state (from PE form)
  const [quickFirmDialogOpen, setQuickFirmDialogOpen] = useState(false);
  const [quickFirmForm, setQuickFirmForm] = useState({ group_id: '', company_name: '', company_email: '', credit_days: 30, contact_persons: [] });
  const [quickFirmSubmitting, setQuickFirmSubmitting] = useState(false);
  const [quickFirmError, setQuickFirmError] = useState('');

  const [expandedMobileId, setExpandedMobileId] = useState(null);

  const [error, setError] = useState('');

  // Sorting hooks
  const { sortedData: sortedPOs, sortKey: poSortKey, sortDirection: poSortDir, handleSort: handlePoSort } = useSortableTable(purchaseOrders, 'po_number', 'asc');
  const { sortedData: sortedPEs, sortKey: peSortKey, sortDirection: peSortDir, handleSort: handlePeSort } = useSortableTable(purchaseEntries, 'date', 'desc');
  const { sortedData: sortedFirms, sortKey: firmSortKey, sortDirection: firmSortDir, handleSort: handleFirmSort } = useSortableTable(allFirms, 'company_name', 'asc');
  const [submitting, setSubmitting] = useState(false);

  usePageCallbacks({
    onNewEntry: () => {
      if (activeTab === 'purchase-orders') { resetPoForm(); setPoDialogOpen(true); }
      else if (activeTab === 'purchase-entries') { resetPeForm(); setPeDialogOpen(true); }
      else if (activeTab === 'suppliers') { openAddFirm(); }
    },
    onPrevTab: () => switchTab('prev'),
    onNextTab: () => switchTab('next'),
    onDateQuick: (key) => {
      const range = getDateRange(key);
      if (activeTab === 'purchase-orders') {
        setPoDateRange({ start_date: range.start_date, end_date: range.end_date });
        setPoQuick(key);
        fetchPurchaseOrders(range.start_date, range.end_date);
      } else if (activeTab === 'purchase-entries') {
        setPeDateRange({ start_date: range.start_date, end_date: range.end_date });
        setPeQuick(key);
        fetchPurchaseEntries(range.start_date, range.end_date);
      }
    },
  });

  // Alt+P = New Product (when PO dialog is open)
  // Alt+S = New Supplier (when PE dialog is open)
  useEffect(() => {
    const handler = (e) => {
      if (e.altKey && e.code === 'KeyP' && poDialogOpen && !productDialogOpen) {
        e.preventDefault();
        openNewProduct();
      }
      if (e.altKey && e.code === 'KeyS' && peDialogOpen && !quickFirmDialogOpen) {
        e.preventDefault();
        openQuickFirm();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [poDialogOpen, productDialogOpen, peDialogOpen, quickFirmDialogOpen]); // eslint-disable-line

  const [dataLoaded, setDataLoaded] = useState(false);
  useEffect(() => { if (activeTab && !dataLoaded) { fetchInitialData(); setDataLoaded(true); } }, [activeTab]); // eslint-disable-line

  const fetchInitialData = async () => {
    try {
      const [groupsRes, productsRes, hsnRes, settingsRes] = await Promise.all([
        getSupplierGroups(),
        getProducts(),
        getHSNCodes(),
        getCompanySettings()
      ]);
      setSupplierGroups(groupsRes.data);
      setProducts(productsRes.data);
      setHsnCodes(hsnRes.data);
      setCompanySettings(settingsRes.data);
      fetchPurchaseOrders(poDateRange.start_date, poDateRange.end_date);
      fetchAllPurchaseOrders();
    } catch (e) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  // ========== PURCHASE ORDERS ==========
  const fetchPurchaseOrders = async (start, end) => {
    try {
      const res = await getPurchaseOrders(null, start, end);
      setPurchaseOrders(res.data);
    } catch (e) { toast.error('Failed to fetch purchase orders'); }
  };

  const fetchAllPurchaseOrders = async () => {
    try {
      const res = await getPurchaseOrders();
      setAllPurchaseOrders(res.data);
    } catch (err) { console.error('Failed to fetch POs:', err); }
  };

  const resetPoForm = () => {
    setPoForm({ supplier_group_id: '', date: getToday(), line_items: [{ ...emptyLineItem }], remarks: '', round_off: '', credit_days: '' });
    setEditingPO(null); setPoError('');
  };

  // GST calculation helper
  const calcGST = (taxableAmount, gstRate, taxType) => {
    const gst = (parseFloat(gstRate) || 0) * (parseFloat(taxableAmount) || 0) / 100;
    if (taxType === 'inter') return { cgst_amount: 0, sgst_amount: 0, igst_amount: Math.round(gst * 100) / 100 };
    const half = Math.round(gst / 2 * 100) / 100;
    return { cgst_amount: half, sgst_amount: half, igst_amount: 0 };
  };

  // Determine tax type based on supplier state vs company state
  const getTaxType = (supplierGroupId) => {
    if (!companySettings.state) return '';
    const group = supplierGroups.find(g => g.id === supplierGroupId);
    if (!group) return '';
    const firm = allFirms.find(f => f.group_id === supplierGroupId && f.state);
    if (!firm) return '';
    return firm.state === companySettings.state ? 'intra' : 'inter';
  };

  // Update a specific line item field
  const updateLineItem = (form, setForm, index, field, value) => {
    setForm(prev => {
      const items = [...prev.line_items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, line_items: items };
    });
  };

  // Add a new line item
  const addLineItem = (setForm) => {
    setForm(prev => ({ ...prev, line_items: [...prev.line_items, { ...emptyLineItem }] }));
  };

  // Remove a line item
  const removeLineItem = (setForm, index) => {
    setForm(prev => {
      const items = prev.line_items.filter((_, i) => i !== index);
      return { ...prev, line_items: items.length ? items : [{ ...emptyLineItem }] };
    });
  };

  // Auto-fill GST for a line item when product changes
  const autoFillLineItemGST = async (form, setForm, index, productId) => {
    const prod = products.find(p => p.id === productId);
    if (!prod) return;
    const taxType = getTaxType(form.supplier_group_id);
    const date = form.date || getToday();
    let gstRate = prod.default_gst_rate || 0;
    if (prod.hsn_code) {
      try {
        const res = await getHSNRateForDate(prod.hsn_code, date);
        gstRate = res.data.gst_rate;
      } catch (err) {
        console.error('HSN rate lookup failed:', err);
        const hsn = hsnCodes.find(h => h.hsn_code === prod.hsn_code);
        if (hsn) gstRate = hsn.gst_rate;
      }
    }
    setForm(prev => {
      const items = [...prev.line_items];
      const li = items[index];
      const taxable = (parseFloat(li.quantity_kg) || 0) * (parseFloat(li.rate) || 0);
      const { cgst_amount, sgst_amount, igst_amount } = calcGST(taxable, gstRate, taxType);
      items[index] = {
        ...li, product_id: productId, hsn_code: prod.hsn_code || '', description: prod.description || '',
        gst_rate: String(gstRate), tax_type: taxType,
        cgst_amount: String(cgst_amount), sgst_amount: String(sgst_amount), igst_amount: String(igst_amount)
      };
      return { ...prev, line_items: items };
    });
  };

  // Auto-fill line item from last PO for this product+supplier (PO form only)
  const autoFillLineItemFromLastPO = async (form, setForm, index, productId) => {
    if (!form.supplier_group_id || !productId) return;
    try {
      const res = await getLastPurchaseOrder(form.supplier_group_id, productId);
      const lastLi = res.data?.line_items?.find(li => li.product_id === productId);
      if (lastLi) {
        setForm(prev => {
          const items = [...prev.line_items];
          const li = items[index];
          const qty = lastLi.quantity_kg ? parseFloat(lastLi.quantity_kg) : 0;
          const rate = lastLi.rate ? parseFloat(lastLi.rate) : 0;
          const taxable = qty * rate;
          const gstRate = li.gst_rate || lastLi.gst_rate || 0;
          const taxType = li.tax_type || lastLi.tax_type || '';
          const { cgst_amount, sgst_amount, igst_amount } = calcGST(taxable, gstRate, taxType);
          items[index] = {
            ...li,
            quantity_kg: lastLi.quantity_kg ? String(lastLi.quantity_kg) : '',
            quantity_bags: lastLi.quantity_bags != null ? String(lastLi.quantity_bags) : '',
            rate: lastLi.rate ? String(lastLi.rate) : '',
            brand: lastLi.brand || '',
            cgst_amount: String(cgst_amount), sgst_amount: String(sgst_amount), igst_amount: String(igst_amount)
          };
          return { ...prev, line_items: items };
        });
      } else {
        setForm(prev => {
          const items = [...prev.line_items];
          items[index] = { ...items[index], quantity_kg: '', quantity_bags: '', rate: '', brand: '', cgst_amount: '', sgst_amount: '', igst_amount: '' };
          return { ...prev, line_items: items };
        });
      }
    } catch (err) {
      console.error('PO remaining fetch failed:', err);
      setForm(prev => {
        const items = [...prev.line_items];
        items[index] = { ...items[index], quantity_kg: '', quantity_bags: '', rate: '', brand: '', cgst_amount: '', sgst_amount: '', igst_amount: '' };
        return { ...prev, line_items: items };
      });
    }
  };

  // Recalculate GST for a line item when qty/rate/gst_rate changes
  const recalcLineItemGST = (form, setForm, index) => {
    setForm(prev => {
      const items = [...prev.line_items];
      const li = items[index];
      const taxable = (parseFloat(li.quantity_kg) || 0) * (parseFloat(li.rate) || 0);
      const { cgst_amount, sgst_amount, igst_amount } = calcGST(taxable, li.gst_rate, li.tax_type);
      items[index] = { ...li, cgst_amount: String(cgst_amount), sgst_amount: String(sgst_amount), igst_amount: String(igst_amount) };
      return { ...prev, line_items: items };
    });
  };

  // Re-lookup HSN rate when date changes for all line items
  const reLookupAllHSNRates = async (form, setForm) => {
    if (!form.date) return;
    const taxType = getTaxType(form.supplier_group_id);
    setForm(prev => {
      const items = prev.line_items.map(li => {
        if (!li.hsn_code) return li;
        return { ...li, tax_type: taxType };
      });
      return { ...prev, line_items: items };
    });
    for (let i = 0; i < form.line_items.length; i++) {
      const li = form.line_items[i];
      if (!li.hsn_code) continue;
      try {
        const res = await getHSNRateForDate(li.hsn_code, form.date);
        const gstRate = res.data.gst_rate;
        setForm(prev => {
          const items = [...prev.line_items];
          const item = items[i];
          const taxable = (parseFloat(item.quantity_kg) || 0) * (parseFloat(item.rate) || 0);
          const { cgst_amount, sgst_amount, igst_amount } = calcGST(taxable, gstRate, taxType);
          items[i] = { ...item, gst_rate: String(gstRate), tax_type: taxType, cgst_amount: String(cgst_amount), sgst_amount: String(sgst_amount), igst_amount: String(igst_amount) };
          return { ...prev, line_items: items };
        });
      } catch (err) { console.error('GST rate refresh failed:', err); }
    }
  };

  // Handle line item kg change with auto bags calculation
  const handleLineItemKgChange = (form, setForm, index, val) => {
    setForm(prev => {
      const items = [...prev.line_items];
      const li = items[index];
      const product = products.find(p => p.id === li.product_id);
      const bags = product && product.kg_per_bag ? Math.round(parseFloat(val) / product.kg_per_bag) : li.quantity_bags;
      items[index] = { ...li, quantity_kg: val, quantity_bags: bags };
      return { ...prev, line_items: items };
    });
    setTimeout(() => recalcLineItemGST(form, setForm, index), 0);
  };

  // Compute totals from line items
  const computeTotals = (lineItems, roundOff) => {
    let subtotal = 0, totalTax = 0;
    lineItems.forEach(li => {
      subtotal += (parseFloat(li.quantity_kg) || 0) * (parseFloat(li.rate) || 0);
      totalTax += (parseFloat(li.cgst_amount) || 0) + (parseFloat(li.sgst_amount) || 0) + (parseFloat(li.igst_amount) || 0);
    });
    const grand = subtotal + totalTax + (parseFloat(roundOff) || 0);
    return { subtotal, totalTax, grand };
  };

  const handlePOGroupChange = async (groupId) => {
    const group = supplierGroups.find(g => g.id === groupId);
    setPoForm(prev => ({ ...prev, supplier_group_id: groupId, credit_days: group ? String(group.credit_days || 30) : prev.credit_days }));
    if (groupId && !editingPO) {
      try {
        const res = await getLastPurchaseOrder(groupId);
        if (res.data && res.data.line_items && res.data.line_items.length > 0) {
          const taxType = getTaxType(groupId);
          const lastItems = res.data.line_items.map(li => {
            const taxable = (parseFloat(li.quantity_kg) || 0) * (parseFloat(li.rate) || 0);
            const gstRate = li.gst_rate || 0;
            const { cgst_amount, sgst_amount, igst_amount } = calcGST(taxable, gstRate, taxType || li.tax_type || '');
            return {
              product_id: li.product_id || '', description: li.description || '', hsn_code: li.hsn_code || '',
              quantity_kg: li.quantity_kg ? String(li.quantity_kg) : '', quantity_bags: li.quantity_bags != null ? String(li.quantity_bags) : '',
              rate: li.rate ? String(li.rate) : '', brand: li.brand || '',
              gst_rate: gstRate ? String(gstRate) : '', tax_type: taxType || li.tax_type || '',
              cgst_amount: String(cgst_amount), sgst_amount: String(sgst_amount), igst_amount: String(igst_amount)
            };
          });
          setPoForm(prev => ({
            ...prev, supplier_group_id: groupId,
            line_items: lastItems,
            remarks: prev.remarks || (res.data.remarks || ''),
          }));
          // If no remarks from this group's last PO, fetch from overall last PO
          if (!res.data.remarks) {
            try {
              const anyRes = await getLastPurchaseOrder('');
              if (anyRes.data?.remarks) setPoForm(prev => ({ ...prev, remarks: prev.remarks || anyRes.data.remarks }));
            } catch (err) { console.error('Fallback remarks fetch failed:', err); }
          }
        }
      } catch (err) { console.error('Supplier group data fetch failed:', err); }
    }
  };

  const handleCreatePO = async (e) => {
    e.preventDefault(); setPoError(''); setPoSubmitting(true);
    if (!isDateInFY(poForm.date, selectedFY)) { setPoError(getFYDateError(selectedFY, fyLabel)); setPoSubmitting(false); return; }
    const validItems = poForm.line_items.filter(li => li.product_id && li.quantity_kg && li.rate);
    if (!validItems.length) { setPoError('At least one complete line item is required'); setPoSubmitting(false); return; }
    try {
      const payload = {
        supplier_group_id: poForm.supplier_group_id,
        date: poForm.date,
        line_items: validItems.map(li => ({
          product_id: li.product_id,
          description: li.description || '',
          hsn_code: li.hsn_code || '',
          quantity_kg: parseFloat(li.quantity_kg),
          quantity_bags: li.quantity_bags ? parseFloat(li.quantity_bags) : null,
          rate: parseFloat(li.rate),
          brand: li.brand || '',
          gst_rate: parseFloat(li.gst_rate) || 0,
          cgst_amount: parseFloat(li.cgst_amount) || 0,
          sgst_amount: parseFloat(li.sgst_amount) || 0,
          igst_amount: parseFloat(li.igst_amount) || 0,
          tax_type: li.tax_type || '',
        })),
        remarks: poForm.remarks,
        round_off: parseFloat(poForm.round_off) || 0,
        credit_days: parseInt(poForm.credit_days) || 0
      };
      if (editingPO) {
        await updatePurchaseOrder(editingPO.id, payload);
        toast.success('Purchase order updated');
      } else {
        await createPurchaseOrder(payload);
        toast.success('Purchase order created');
      }
      setPoDialogOpen(false); resetPoForm();
      fetchPurchaseOrders(poDateRange.start_date, poDateRange.end_date);
      fetchAllPurchaseOrders();
    } catch (err) { setPoError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setPoSubmitting(false); }
  };

  const openEditPO = (po) => {
    setEditingPO(po);
    const lineItems = (po.line_items || []).map(li => ({
      product_id: li.product_id || '', description: li.description || '', hsn_code: li.hsn_code || '',
      quantity_kg: String(li.quantity_kg || ''), quantity_bags: li.quantity_bags != null ? String(li.quantity_bags) : '',
      rate: String(li.rate || ''), brand: li.brand || '',
      gst_rate: String(li.gst_rate || ''), cgst_amount: String(li.cgst_amount || ''),
      sgst_amount: String(li.sgst_amount || ''), igst_amount: String(li.igst_amount || ''),
      tax_type: li.tax_type || ''
    }));
    setPoForm({
      supplier_group_id: po.supplier_group_id, date: po.date,
      line_items: lineItems.length ? lineItems : [{ ...emptyLineItem }],
      remarks: po.remarks || '', round_off: String(po.round_off || '')
    });
    setPoDialogOpen(true);
  };

  const handleDeletePO = async (po) => {
    if (!window.confirm('Delete this purchase order?')) return;
    try { await deletePurchaseOrder(po.id); toast.success('Deleted'); fetchPurchaseOrders(poDateRange.start_date, poDateRange.end_date); fetchAllPurchaseOrders(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // ========== PURCHASE ENTRIES ==========
  const fetchPurchaseEntries = async (start, end) => {
    try {
      const res = await getPurchaseEntries(null, null, start, end);
      setPurchaseEntries(res.data);
    } catch (e) { toast.error('Failed to fetch entries'); }
  };

  const resetPeForm = () => {
    setPeForm({ purchase_order_id: '', supplier_id: '', date: getToday(), line_items: [{ ...emptyLineItem }], invoice_number: '', vehicle_number: '', remarks: '', round_off: '', credit_days: '' });
    setPeError(''); setSelectedPOForEntry(null);
  };

  const handlePePoChange = async (poId) => {
    setPeForm(prev => ({ ...prev, purchase_order_id: poId }));
    if (poId) {
      const po = allPurchaseOrders.find(o => o.id === poId);
      if (po) {
        setSelectedPOForEntry(po);
        // Pre-fill line items from PO's line items
        const poItems = (po.line_items || []).map(li => ({
          product_id: li.product_id || '', description: li.description || '', hsn_code: li.hsn_code || '',
          quantity_kg: '', quantity_bags: '', rate: String(li.rate || ''), brand: li.brand || '',
          gst_rate: String(li.gst_rate || ''), cgst_amount: '', sgst_amount: '', igst_amount: '',
          tax_type: li.tax_type || ''
        }));
        setPeForm(prev => ({
          ...prev, purchase_order_id: poId,
          line_items: poItems.length ? poItems : [{ ...emptyLineItem }]
        }));
        try {
          const res = await getSuppliers(po.supplier_group_id);
          setSuppliers(res.data);
        } catch (err) { console.error('Supplier fetch for PO edit failed:', err); }
      }
    }
  };

  const handleDeletePE = async (entry) => {
    if (!window.confirm('Delete this purchase entry?')) return;
    try {
      await deletePurchaseEntry(entry.id); toast.success('Deleted');
      fetchPurchaseEntries(peDateRange.start_date, peDateRange.end_date);
      fetchPurchaseOrders(poDateRange.start_date, poDateRange.end_date);
      fetchAllPurchaseOrders();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const [editingPE, setEditingPE] = useState(null);

  const openEditPE = async (entry) => {
    setEditingPE(entry);
    const lineItems = (entry.line_items || []).map(li => ({
      product_id: li.product_id || '', description: li.description || '', hsn_code: li.hsn_code || '',
      quantity_kg: String(li.quantity_kg || ''), quantity_bags: li.quantity_bags != null ? String(li.quantity_bags) : '',
      rate: String(li.rate || ''), brand: li.brand || '',
      gst_rate: String(li.gst_rate || ''), cgst_amount: String(li.cgst_amount || ''),
      sgst_amount: String(li.sgst_amount || ''), igst_amount: String(li.igst_amount || ''),
      tax_type: li.tax_type || ''
    }));
    setPeForm({
      purchase_order_id: entry.purchase_order_id, supplier_id: entry.supplier_id,
      date: entry.date,
      line_items: lineItems.length ? lineItems : [{ ...emptyLineItem }],
      invoice_number: entry.invoice_number || '', vehicle_number: entry.vehicle_number || '',
      remarks: entry.remarks || '', round_off: String(entry.round_off || ''),
      credit_days: entry.credit_days != null ? String(entry.credit_days) : ''
    });
    const po = allPurchaseOrders.find(o => o.id === entry.purchase_order_id);
    if (po) {
      setSelectedPOForEntry(po);
      try { const res = await getSuppliers(po.supplier_group_id); setSuppliers(res.data); } catch (err) { console.error('Supplier fetch for PE edit failed:', err); }
    }
    setPeError(''); setPeDialogOpen(true);
  };

  const handleSubmitPE = async (e) => {
    e.preventDefault(); setPeError(''); setPeSubmitting(true);
    if (!isDateInFY(peForm.date, selectedFY)) { setPeError(getFYDateError(selectedFY, fyLabel)); setPeSubmitting(false); return; }
    const validItems = peForm.line_items.filter(li => li.product_id && li.quantity_kg && li.rate);
    if (!validItems.length) { setPeError('At least one complete line item is required'); setPeSubmitting(false); return; }
    try {
      const payload = {
        purchase_order_id: peForm.purchase_order_id, supplier_id: peForm.supplier_id,
        date: peForm.date,
        line_items: validItems.map(li => ({
          product_id: li.product_id,
          description: li.description || '',
          hsn_code: li.hsn_code || '',
          quantity_kg: parseFloat(li.quantity_kg),
          quantity_bags: li.quantity_bags ? parseFloat(li.quantity_bags) : null,
          rate: parseFloat(li.rate),
          brand: li.brand || '',
          gst_rate: parseFloat(li.gst_rate) || 0,
          cgst_amount: parseFloat(li.cgst_amount) || 0,
          sgst_amount: parseFloat(li.sgst_amount) || 0,
          igst_amount: parseFloat(li.igst_amount) || 0,
          tax_type: li.tax_type || '',
        })),
        invoice_number: peForm.invoice_number, vehicle_number: peForm.vehicle_number,
        remarks: peForm.remarks, round_off: parseFloat(peForm.round_off) || 0,
        credit_days: parseInt(peForm.credit_days) || 0,
      };
      if (editingPE) {
        await updatePurchaseEntry(editingPE.id, payload);
        toast.success('Purchase entry updated');
      } else {
        await createPurchaseEntry(payload);
        toast.success('Purchase entry created');
      }
      setPeDialogOpen(false); resetPeForm(); setEditingPE(null);
      fetchPurchaseEntries(peDateRange.start_date, peDateRange.end_date);
      fetchPurchaseOrders(poDateRange.start_date, poDateRange.end_date);
      fetchAllPurchaseOrders();
    } catch (err) { setPeError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setPeSubmitting(false); }
  };

  // ========== BULK OPERATIONS ==========
  const togglePoSelect = (id) => setSelectedPoIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const togglePeSelect = (id) => setSelectedPeIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleFirmSelect = (id) => setSelectedFirmIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const allPoSelected = purchaseOrders.length > 0 && purchaseOrders.every(po => selectedPoIds.includes(po.id));
  const allPeSelected = purchaseEntries.length > 0 && purchaseEntries.every(pe => selectedPeIds.includes(pe.id));
  const allFirmsSelected = allFirms.length > 0 && allFirms.every(f => selectedFirmIds.includes(f.id));
  const toggleAllPo = () => { if (allPoSelected) setSelectedPoIds([]); else setSelectedPoIds(sortedPOs.map(po => po.id)); };
  const toggleAllPe = () => { if (allPeSelected) setSelectedPeIds([]); else setSelectedPeIds(sortedPEs.map(pe => pe.id)); };
  const toggleAllFirms = () => { if (allFirmsSelected) setSelectedFirmIds([]); else setSelectedFirmIds(sortedFirms.map(f => f.id)); };

  const handleBulkDeletePOs = async () => {
    if (!window.confirm(`Delete ${selectedPoIds.length} purchase orders?`)) return;
    try {
      const res = await bulkDeletePurchaseOrders(selectedPoIds);
      const msg = res.data?.message || 'Deleted';
      const skippedLen = Array.isArray(res.data?.skipped) ? res.data.skipped.length : (res.data?.skipped || 0);
      if (skippedLen > 0) toast.warning(msg);
      else toast.success(msg);
      setSelectedPoIds([]);
      fetchPurchaseOrders(poDateRange.start_date, poDateRange.end_date);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const handleBulkDeletePEs = async () => {
    if (!window.confirm(`Delete ${selectedPeIds.length} purchase entries?`)) return;
    try {
      const res = await bulkDeletePurchaseEntries(selectedPeIds);
      const msg = res.data?.message || 'Deleted';
      const skippedLen = Array.isArray(res.data?.skipped) ? res.data.skipped.length : (res.data?.skipped || 0);
      if (skippedLen > 0) toast.warning(msg);
      else toast.success(msg);
      setSelectedPeIds([]);
      fetchPurchaseEntries(peDateRange.start_date, peDateRange.end_date);
      fetchPurchaseOrders(poDateRange.start_date, poDateRange.end_date);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const handleBulkDeleteFirms = async () => {
    if (!window.confirm(`Delete ${selectedFirmIds.length} suppliers?`)) return;
    try {
      const res = await bulkDeleteSuppliers(selectedFirmIds);
      const msg = res.data?.message || 'Deleted';
      const skippedLen = Array.isArray(res.data?.skipped) ? res.data.skipped.length : (res.data?.skipped || 0);
      if (skippedLen > 0) toast.warning(msg);
      else toast.success(msg);
      setSelectedFirmIds([]);
      fetchAllFirms();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // ========== OUTSTANDING ==========
  const fetchOutstanding = async () => {
    setOutstandingLoading(true);
    try { const res = await getPurchaseOutstanding(); setOutstanding(res.data); }
    catch (e) { toast.error('Failed to load outstanding'); }
    finally { setOutstandingLoading(false); }
  };

  // ========== PAYMENT OUTSTANDING ==========
  const fetchPaymentOutstanding = async () => {
    setPaymentOutstandingLoading(true);
    try { const res = await getPurchasePaymentOutstanding(); setPaymentOutstanding(res.data); }
    catch (e) { toast.error('Failed to load payment outstanding'); }
    finally { setPaymentOutstandingLoading(false); }
  };

  // ========== UNIFIED PAYMENTS ==========
  const fetchPayments = async (start, end) => {
    setPaymentsLoading(true);
    const s = start ?? paymentDateRange.start_date;
    const e = end ?? paymentDateRange.end_date;
    try { const res = await getPurchasePayments(undefined, undefined, s, e); setPayments(res.data); }
    catch (err) { toast.error('Failed to load payments'); }
    finally { setPaymentsLoading(false); }
  };
  // Get firm options from outstanding data (with credit days)
  const payFirmOptions = (paymentOutstanding?.firms || []).map(f => {
    const supplier = suppliers.find(s => s.id === f.supplier_id) || {};
    return { value: f.supplier_id, label: `${f.supplier_name} (${supplier.credit_days || 0}d credit)` };
  });
  const modeOptions = [
    { value: 'online', label: 'Online / NEFT / RTGS' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'cash', label: 'Cash' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
  ];
  const openPayDialog = () => {
    setPayForm({ supplier_id: '', amount: '', date: getToday(), mode: 'online', reference: '', bank_name: '', remarks: '', allocations: [] });
    setPayError('');
    setPayDialogOpen(true);
  };
  const selectPayFirm = (supplierId) => {
    const firm = (paymentOutstanding?.firms || []).find(f => f.supplier_id === supplierId);
    if (!firm) { setPayForm(prev => ({ ...prev, supplier_id: supplierId, allocations: [] })); return; }
    const allocs = (firm.entries || []).map(e => ({
      purchase_entry_id: e.id, invoice_number: e.invoice_number || '-',
      outstanding: e.outstanding, due_date: e.due_date, amount: ''
    }));
    setPayForm(prev => ({ ...prev, supplier_id: supplierId, amount: '', allocations: allocs }));
  };
  const updatePayAmount = (value) => {
    const amt = parseFloat(value) || 0;
    let remaining = amt;
    setPayForm(prev => {
      const allocs = prev.allocations.map(a => {
        if (remaining <= 0) return { ...a, amount: '' };
        const alloc = Math.min(remaining, a.outstanding);
        remaining -= alloc;
        return { ...a, amount: String(alloc) };
      });
      return { ...prev, amount: value, allocations: allocs };
    });
  };
  const updateAllocation = (idx, value) => {
    setPayForm(prev => {
      const allocs = [...prev.allocations];
      allocs[idx] = { ...allocs[idx], amount: value };
      const total = allocs.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
      return { ...prev, allocations: allocs, amount: String(total) };
    });
  };
  const handlePaySubmit = async (e) => {
    e.preventDefault();
    setPayError('');
    setPaySubmitting(true);
    if (!payForm.supplier_id) { setPayError('Select a supplier'); setPaySubmitting(false); return; }
    const amt = parseFloat(payForm.amount);
    if (!amt || amt <= 0) { setPayError('Enter a valid amount'); setPaySubmitting(false); return; }
    if (payForm.mode === 'cheque' && !payForm.reference.trim()) { setPayError('Cheque number is required'); setPaySubmitting(false); return; }
    const validAllocs = payForm.allocations.filter(a => parseFloat(a.amount) > 0).map(a => ({ purchase_entry_id: a.purchase_entry_id, amount: parseFloat(a.amount) }));
    if (validAllocs.length === 0) { setPayError('Allocate amount to at least one invoice'); setPaySubmitting(false); return; }
    const allocTotal = validAllocs.reduce((s, a) => s + a.amount, 0);
    if (Math.abs(allocTotal - amt) > 0.01) { setPayError(`Allocation (${formatCurrency(allocTotal)}) doesn't match amount (${formatCurrency(amt)})`); setPaySubmitting(false); return; }
    try {
      await createPurchasePayment({
        supplier_id: payForm.supplier_id, amount: amt, date: payForm.date,
        mode: payForm.mode, reference: payForm.reference, bank_name: payForm.bank_name,
        remarks: payForm.remarks, allocations: validAllocs
      });
      toast.success(payForm.mode === 'cheque' ? 'Cheque recorded (pending clearance)' : 'Payment recorded');
      setPayDialogOpen(false);
      fetchPaymentOutstanding();
      if (activeTab === 'payments') fetchPayments();
    } catch (err) { setPayError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setPaySubmitting(false); }
  };
  const handleClearPayment = async () => {
    if (!clearingPayment) return;
    setClearSubmitting(true);
    try {
      await clearPurchasePayment(clearingPayment.id, { clear_date: clearDate });
      toast.success('Cheque cleared & payment applied');
      setClearDialogOpen(false);
      setClearingPayment(null);
      fetchPayments();
      fetchPaymentOutstanding();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setClearSubmitting(false); }
  };
  const handleBouncePayment = async (p) => {
    if (!window.confirm(`Mark cheque #${p.reference} as bounced?`)) return;
    try { await bouncePurchasePayment(p.id); toast.success('Payment marked as bounced'); fetchPayments(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const handleDeletePayment = async (p) => {
    if (!window.confirm(`Delete payment?`)) return;
    try { await deletePurchasePayment(p.id); toast.success('Payment deleted'); fetchPayments(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const allPaySelected = payments.length > 0 && payments.filter(p => p.status === 'pending' || p.status === 'bounced').every(p => selectedPayIds.includes(p.id));
  const togglePaySelect = (id) => setSelectedPayIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAllPay = () => {
    const deletable = payments.filter(p => p.status === 'pending' || p.status === 'bounced');
    if (allPaySelected) setSelectedPayIds([]); else setSelectedPayIds(deletable.map(p => p.id));
  };
  const handleBulkDeletePayments = async () => {
    if (!window.confirm(`Delete ${selectedPayIds.length} selected payments?`)) return;
    let deleted = 0;
    for (const id of selectedPayIds) {
      try { await deletePurchasePayment(id); deleted++; } catch (err) { console.error(`Failed to delete payment ${id}:`, err); }
    }
    toast.success(`Deleted ${deleted} payments`);
    setSelectedPayIds([]);
    fetchPayments();
  };

  // ========== SUPPLIERS ==========
  const fetchAllFirms = async () => {
    try {
      const [firmsRes, groupsRes] = await Promise.all([getSuppliers(), getSupplierGroups()]);
      setAllFirms(firmsRes.data);
      setSupplierGroups(groupsRes.data);
    } catch (e) { toast.error('Failed to load suppliers'); }
  };

  const openAddFirm = () => {
    setEditingFirm(null);
    setFirmForm({ group_id: '', company_name: '', company_email: '', contact_persons: [{ ...emptyContact }], credit_days: '30', gstin: '', state: '', state_code: '' });
    setFirmError(''); setFirmDialogOpen(true);
  };

  const openEditFirm = (firm) => {
    setEditingFirm(firm);
    setFirmForm({
      group_id: firm.group_id, company_name: firm.company_name, company_email: firm.company_email || '',
      contact_persons: firm.contact_persons?.length ? firm.contact_persons : [{ ...emptyContact }],
      credit_days: firm.credit_days?.toString() || '30',
      gstin: firm.gstin || '', state: firm.state || '', state_code: firm.state_code || ''
    });
    setFirmError(''); setFirmDialogOpen(true);
  };

  const handleCreateGroupFromSelect = async (name, context) => {
    try {
      const res = await createSupplierGroup({ name });
      toast.success('Group created');
      const groupsRes = await getSupplierGroups();
      setSupplierGroups(groupsRes.data);
      if (context === 'po') {
        setPoForm(p => ({ ...p, supplier_group_id: res.data.id }));
      } else if (context === 'quickfirm') {
        setQuickFirmForm(p => ({ ...p, group_id: res.data.id }));
      } else {
        setFirmForm(p => ({ ...p, group_id: res.data.id }));
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const handleSaveGroupEdit = async (groupId) => {
    if (!editGroupName.trim()) return;
    try {
      await updateSupplierGroup(groupId, { name: editGroupName.trim(), credit_days: parseInt(editGroupCreditDays) || 30 });
      toast.success('Group updated');
      setEditingGroupId(null);
      const groupsRes = await getSupplierGroups();
      setSupplierGroups(groupsRes.data);
      fetchAllFirms();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Delete group "${group.name}"?`)) return;
    try {
      await deleteSupplierGroup(group.id);
      toast.success('Group deleted');
      const groupsRes = await getSupplierGroups();
      setSupplierGroups(groupsRes.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openNewProduct = () => {
    setProductForm({ name: '', category: '', group: '', kg_per_bag: '', brands: '', hsn_code: '', default_gst_rate: '', description: '' });
    setProductError('');
    setProductDialogOpen(true);
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    setProductError(''); setProductSubmitting(true);
    try {
      const payload = {
        name: productForm.name,
        category: productForm.category,
        group: productForm.group,
        kg_per_bag: productForm.kg_per_bag ? parseFloat(productForm.kg_per_bag) : 50.0,
        brands: productForm.brands ? productForm.brands.split(',').map(b => b.trim()).filter(Boolean) : [],
        hsn_code: productForm.hsn_code || '',
        default_gst_rate: productForm.default_gst_rate ? parseFloat(productForm.default_gst_rate) : 0,
        description: productForm.description || ''
      };
      const res = await createProduct(payload);
      toast.success('Product created');
      setProductDialogOpen(false);
      const productsRes = await getProducts();
      setProducts(productsRes.data);
      setPoForm(p => ({ ...p, product_id: res.data.id }));
    } catch (err) { setProductError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setProductSubmitting(false); }
  };

  const openQuickFirm = () => {
    // Pre-select the group from the selected PO's supplier_group_id
    const groupId = selectedPOForEntry?.supplier_group_id || '';
    setQuickFirmForm({ group_id: groupId, company_name: '', company_email: '', credit_days: 30, contact_persons: [] });
    setQuickFirmError('');
    // Auto-fetch contacts from existing firms in same group (check both suppliers and allFirms)
    if (groupId) {
      const existingFirm = suppliers.find(f => f.group_id === groupId) || allFirms.find(f => f.group_id === groupId);
      if (existingFirm && existingFirm.contact_persons?.length > 0) {
        setQuickFirmForm(p => ({ ...p, contact_persons: existingFirm.contact_persons.map(cp => ({ ...cp })) }));
      }
    }
    setQuickFirmDialogOpen(true);
  };

  const handleQuickFirmGroupChange = (groupId) => {
    setQuickFirmForm(p => ({ ...p, group_id: groupId }));
    const existingFirm = suppliers.find(f => f.group_id === groupId) || allFirms.find(f => f.group_id === groupId);
    if (existingFirm && existingFirm.contact_persons?.length > 0) {
      setQuickFirmForm(p => ({ ...p, contact_persons: existingFirm.contact_persons.map(cp => ({ ...cp })) }));
    } else {
      setQuickFirmForm(p => ({ ...p, contact_persons: [] }));
    }
  };

  const handleCreateQuickFirm = async (e) => {
    e.preventDefault();
    setQuickFirmError(''); setQuickFirmSubmitting(true);
    try {
      const payload = { ...quickFirmForm, credit_days: parseInt(quickFirmForm.credit_days) || 30, contact_persons: quickFirmForm.contact_persons.filter(cp => cp.name) };
      const res = await createSupplier(payload);
      toast.success('Supplier created');
      setQuickFirmDialogOpen(false);
      fetchAllFirms();
      setPeForm(p => ({ ...p, supplier_id: res.data.id }));
    } catch (err) { setQuickFirmError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setQuickFirmSubmitting(false); }
  };

  const handleCreateFirm = async (e) => {
    e.preventDefault(); setFirmError(''); setFirmSubmitting(true);
    try {
      const payload = { ...firmForm, credit_days: parseInt(firmForm.credit_days) };
      if (editingFirm) {
        await updateSupplier(editingFirm.id, payload);
        toast.success('Firm updated');
      } else {
        await createSupplier(payload);
        toast.success('Firm created');
      }
      setFirmDialogOpen(false);
      fetchAllFirms();
    } catch (err) { setFirmError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setFirmSubmitting(false); }
  };

  const handleDeleteFirm = async (firm) => {
    if (!window.confirm(`Delete "${firm.company_name}"?`)) return;
    try { await deleteSupplier(firm.id); toast.success('Deleted'); fetchAllFirms(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // Tab change effect
  useEffect(() => {
    if (activeTab === 'purchase-entries') fetchPurchaseEntries(peDateRange.start_date, peDateRange.end_date);
    if (activeTab === 'outstanding') fetchOutstanding();
    if (activeTab === 'payment-outstanding') fetchPaymentOutstanding();
    if (activeTab === 'payments') { fetchPayments(paymentDateRange.start_date, paymentDateRange.end_date); if (!paymentOutstanding) fetchPaymentOutstanding(); }
    if (activeTab === 'suppliers') fetchAllFirms();
  }, [activeTab]); // eslint-disable-line

  const groupOptions = supplierGroups.map(g => ({ value: g.id, label: g.name }));
  const productOptions = products.map(p => ({ value: p.id, label: `${p.name} (${p.group})` }));
  const poOptions = allPurchaseOrders.filter(o => o.status !== 'completed').map(o => {
    const firstProduct = o.line_items?.[0]?.product_name || o.product_name || '';
    const itemCount = (o.line_items || []).length;
    const label = `${o.po_number || 'PO'} — ${o.supplier_group_name} - ${firstProduct}${itemCount > 1 ? ` +${itemCount-1}` : ''} (${formatDate(o.date)})`;
    return { value: o.id, label };
  });
  const supplierOptions = suppliers.map(s => ({ value: s.id, label: s.company_name }));

  // Print single document as PDF
  const printSingle = async (path, fileName) => {
    try {
      const res = await api.get(path, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${fileName}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1500);
    } catch (e) { toast.error('Failed to generate PDF'); }
  };

  if (loading && activeTab) return <div className="space-y-6"><Skeleton className="h-8 w-32" /><Skeleton className="h-64 w-full" /></div>;

  return (
    <>
    <SectionTiles
      sections={SECTION_DEFS}
      activeSection={activeTab}
      onSelect={(v) => {
        if (v === 'supplier-ledger') { navigate('/ledger', { state: { tab: 'supplier-ledger' } }); return; }
        if (v === 'notes') { navigate('/purchase/debit-notes'); return; }
        setActiveTab(v); setSelectedPoIds([]); setSelectedPeIds([]); setSelectedFirmIds([]);
      }}
      pageTitle="Purchase"
      pageDescription="Manage purchase orders, entries, and suppliers"
      headerActions={(() => {
        const ExportBtn = ({ ep, name, params }) => (
          <ExportToolbar endpoint={ep} fileBaseName={name} params={params || {}} testId={`export-${activeTab}`} />
        );
        switch (activeTab) {
          case 'purchase-orders': return (
            <div className="flex gap-2">
              <ExportBtn ep="/exports/purchase-orders" name={`purchase-orders-${poDateRange.start_date}_to_${poDateRange.end_date}`} params={{ start_date: poDateRange.start_date, end_date: poDateRange.end_date }} />
              <Button size="sm" onClick={() => { resetPoForm(); setPoDialogOpen(true); }} data-testid="add-po-btn"><Plus className="h-4 w-4 mr-1" />Add PO</Button>
            </div>
          );
          case 'purchase-entries': return (
            <div className="flex gap-2">
              <ExportBtn ep="/exports/purchase-entries" name={`purchase-${peDateRange.start_date}_to_${peDateRange.end_date}`} params={{ start_date: peDateRange.start_date, end_date: peDateRange.end_date }} />
              <Button size="sm" onClick={() => { resetPeForm(); setPeDialogOpen(true); }} data-testid="add-pe-btn"><Plus className="h-4 w-4 mr-1" />Add Entry</Button>
            </div>
          );
          case 'outstanding': return <ExportBtn ep="/exports/po-outstanding" name="po-outstanding" />;
          case 'payment-outstanding': return (
            <div className="flex gap-2">
              <ExportBtn ep="/exports/purchase-payment-outstanding" name="purchase-outstanding" />
              <Button size="sm" onClick={() => openPayDialog()} data-testid="record-payment-btn"><IndianRupee className="h-4 w-4 mr-1" />Record Payment</Button>
            </div>
          );
          case 'payments': return (
            <div className="flex gap-2">
              <ExportBtn ep="/exports/payments" name={`payments-${paymentDateRange.start_date}_to_${paymentDateRange.end_date}`} params={{ start_date: paymentDateRange.start_date, end_date: paymentDateRange.end_date }} />
              <Button size="sm" onClick={() => openPayDialog()} data-testid="record-payment-btn2"><IndianRupee className="h-4 w-4 mr-1" />Record Payment</Button>
            </div>
          );
          case 'suppliers': return <Button size="sm" onClick={() => openGroupDialog()} data-testid="add-group-btn"><Plus className="h-4 w-4 mr-1" />Add Group</Button>;
          default: return null;
        }
      })()}
    >

      {activeTab === 'purchase-orders' && (
        <div className="space-y-4">
          <DateRangeFilter startDate={poDateRange.start_date} endDate={poDateRange.end_date}
            onStartDateChange={(v) => setPoDateRange(prev => ({...prev, start_date: v}))}
            onEndDateChange={(v) => setPoDateRange(prev => ({...prev, end_date: v}))}
            onApply={(s, e) => { setPoDateRange({start_date: s, end_date: e}); fetchPurchaseOrders(s, e); }}
            activeQuick={poQuick} onQuickSelect={setPoQuick} />

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 cursor-pointer" onClick={toggleAllPo}><Checkbox checked={allPoSelected} className="pointer-events-none" data-testid="select-all-po" /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="PO #" sortKey="po_number" currentSortKey={poSortKey} sortDirection={poSortDir} onSort={handlePoSort} /></TableHead>
                      <TableHead><SortableHeader label="Date" sortKey="date" currentSortKey={poSortKey} sortDirection={poSortDir} onSort={handlePoSort} /></TableHead>
                      <TableHead><SortableHeader label="Group" sortKey="supplier_group_name" currentSortKey={poSortKey} sortDirection={poSortDir} onSort={handlePoSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="Product" sortKey="product_name" currentSortKey={poSortKey} sortDirection={poSortDir} onSort={handlePoSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell">Brand</TableHead>
                      <TableHead className="text-right"><SortableHeader label="Quantity" sortKey="quantity_kg" currentSortKey={poSortKey} sortDirection={poSortDir} onSort={handlePoSort} className="justify-end" /></TableHead>
                      <TableHead className="text-right hidden sm:table-cell">Rate</TableHead>
                      <TableHead className="text-right hidden sm:table-cell"><SortableHeader label="Basic Amount" sortKey="amount" currentSortKey={poSortKey} sortDirection={poSortDir} onSort={handlePoSort} className="justify-end" /></TableHead>
                      <TableHead className="text-right hidden sm:table-cell"><SortableHeader label="Total" sortKey="grand_total" currentSortKey={poSortKey} sortDirection={poSortDir} onSort={handlePoSort} className="justify-end" /></TableHead>
                      <TableHead className="text-right hidden sm:table-cell"><SortableHeader label="Received" sortKey="received_kg" currentSortKey={poSortKey} sortDirection={poSortDir} onSort={handlePoSort} className="justify-end" /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="Status" sortKey="status" currentSortKey={poSortKey} sortDirection={poSortDir} onSort={handlePoSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell">Remarks</TableHead>
                      <TableHead className="w-[60px] sm:w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedPOs.length === 0 ? (
                      <TableRow><TableCell colSpan={13} className="text-center py-8 text-muted-foreground">No purchase orders found</TableCell></TableRow>
                    ) : sortedPOs.map(po => {
                      const li = po.line_items || [];
                      const firstItem = li[0] || {};
                      const productDisplay = li.length > 1 ? `${firstItem.product_name || ''} +${li.length-1}` : (firstItem.product_name || po.product_name || '-');
                      const brandDisplay = li.length === 1 ? (firstItem.brand || '-') : (li.length > 1 ? `${li.filter(i => i.brand).length} brands` : '-');
                      return (
                      <React.Fragment key={po.id}>
                        <TableRow
                          className={`cursor-pointer sm:cursor-default ${selectedPoIds.includes(po.id) ? 'bg-muted/50' : ''} ${expandedMobileId === po.id ? 'bg-muted/30' : ''}`}
                          onClick={() => setExpandedMobileId(expandedMobileId === po.id ? null : po.id)}
                          data-testid={`po-row-${po.id}`}
                        >
                          <TableCell className="cursor-pointer" onClick={(e) => { e.stopPropagation(); togglePoSelect(po.id); }}><Checkbox checked={selectedPoIds.includes(po.id)} className="pointer-events-none" /></TableCell>
                          <TableCell className="font-mono text-xs hidden sm:table-cell">{po.po_number || '-'}</TableCell>
                          <TableCell className="text-sm">{formatDate(po.date)}</TableCell>
                          <TableCell className="font-medium text-sm">{po.supplier_group_name}</TableCell>
                          <TableCell className="hidden sm:table-cell">{productDisplay}</TableCell>
                          <TableCell className="hidden sm:table-cell text-sm">{brandDisplay}</TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            <div>{(po.quantity_kg || 0).toLocaleString()} KG</div>
                            <div className="text-xs text-muted-foreground">{toTons(po.quantity_kg)}</div>
                          </TableCell>
                          <TableCell className="text-right font-mono hidden sm:table-cell">{formatCurrency(firstItem.rate || po.rate || 0)}</TableCell>
                          <TableCell className="text-right font-mono hidden sm:table-cell">{formatCurrency(po.amount || (po.quantity_kg * (firstItem.rate || po.rate || 0)))}</TableCell>
                          <TableCell className="text-right font-mono hidden sm:table-cell">{formatCurrency(po.grand_total || po.amount || 0)}</TableCell>
                          <TableCell className="text-right font-mono hidden sm:table-cell">{(po.received_kg || 0).toLocaleString()} KG</TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <Badge variant={po.status === 'completed' ? 'default' : po.status === 'partial' ? 'secondary' : 'outline'}>{po.status}</Badge>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell text-sm max-w-[200px]"><span className="whitespace-pre-wrap">{po.remarks || '-'}</span></TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Print PDF" onClick={(e) => { e.stopPropagation(); printSingle(`/exports/purchase-orders/${po.id}/pdf`, po.po_number || 'purchase-order'); }} data-testid={`print-po-${po.id}`}><Printer className="h-3 w-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditPO(po)} data-testid={`edit-po-${po.id}`}><Pencil className="h-3 w-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeletePO(po)} data-testid={`delete-po-${po.id}`}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {expandedMobileId === po.id && (
                          <TableRow className="sm:hidden bg-muted/20">
                            <TableCell colSpan={13}>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm py-1">
                                <span className="text-muted-foreground">PO #</span><span className="text-right font-mono">{po.po_number || '-'}</span>
                                {li.map((item, idx) => (
                                  <React.Fragment key={idx}>
                                    <span className="text-muted-foreground">Item {idx + 1}</span>
                                    <span className="text-right">{item.product_name} {item.brand ? `[${item.brand}]` : ''}</span>
                                    <span className="text-muted-foreground pl-2">Qty/Rate</span>
                                    <span className="text-right font-mono">{item.quantity_kg?.toLocaleString()} KG @ {formatCurrency(item.rate)}</span>
                                  </React.Fragment>
                                ))}
                                <span className="text-muted-foreground">Amount</span><span className="text-right">{formatCurrency(po.grand_total || po.amount || 0)}</span>
                                <span className="text-muted-foreground">Received</span><span className="text-right font-mono">{(po.received_kg || 0).toLocaleString()} KG</span>
                                <span className="text-muted-foreground">Status</span><span className="text-right"><Badge variant={po.status === 'completed' ? 'default' : 'outline'} className="text-xs">{po.status}</Badge></span>
                                {po.remarks && <><span className="text-muted-foreground">Remarks</span><span className="text-right">{po.remarks}</span></>}
                                <span className="text-muted-foreground">Remaining</span><span className="text-right font-mono">{(po.remaining_kg || 0).toLocaleString()} KG</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    )})}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

        </div>
      )}

        {/* ========== PURCHASE ENTRIES TAB ========== */}
      {activeTab === 'purchase-entries' && (
        <div className="space-y-4">
          <DateRangeFilter startDate={peDateRange.start_date} endDate={peDateRange.end_date}
            onStartDateChange={(v) => setPeDateRange(prev => ({...prev, start_date: v}))}
            onEndDateChange={(v) => setPeDateRange(prev => ({...prev, end_date: v}))}
            onApply={(s, e) => { setPeDateRange({start_date: s, end_date: e}); fetchPurchaseEntries(s, e); }}
            activeQuick={peQuick} onQuickSelect={setPeQuick} />

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 cursor-pointer" onClick={toggleAllPe}><Checkbox checked={allPeSelected} className="pointer-events-none" data-testid="select-all-pe" /></TableHead>
                      <TableHead><SortableHeader label="Date" sortKey="date" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} /></TableHead>
                      <TableHead><SortableHeader label="Supplier" sortKey="supplier_name" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="Product" sortKey="product_name" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} /></TableHead>
                      <TableHead className="text-right"><SortableHeader label="Quantity" sortKey="quantity_kg" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} className="justify-end" /></TableHead>
                      <TableHead className="text-right hidden sm:table-cell"><SortableHeader label="Amount" sortKey="amount" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} className="justify-end" /></TableHead>
                      <TableHead className="text-right hidden sm:table-cell"><SortableHeader label="Total" sortKey="grand_total" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} className="justify-end" /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="Invoice" sortKey="invoice_number" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="Vehicle" sortKey="vehicle_number" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="Brand" sortKey="brand" currentSortKey={peSortKey} sortDirection={peSortDir} onSort={handlePeSort} /></TableHead>
                      <TableHead className="w-[60px] sm:w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedPEs.length === 0 ? (
                      <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">No purchase entries found</TableCell></TableRow>
                    ) : sortedPEs.map(entry => {
                      const li = entry.line_items || [];
                      const firstItem = li[0] || {};
                      const productDisplay = li.length > 1 ? `${firstItem.product_name || ''} +${li.length-1}` : (firstItem.product_name || entry.product_name || '-');
                      const brandDisplay = li.length === 1 ? (firstItem.brand || '-') : (li.some(i => i.brand) ? `${li.filter(i => i.brand).length} brands` : '-');
                      return (
                      <React.Fragment key={entry.id}>
                        <TableRow
                          className={`cursor-pointer sm:cursor-default ${selectedPeIds.includes(entry.id) ? 'bg-muted/50' : ''} ${expandedMobileId === entry.id ? 'bg-muted/30' : ''}`}
                          onClick={() => setExpandedMobileId(expandedMobileId === entry.id ? null : entry.id)}
                          data-testid={`pe-row-${entry.id}`}
                        >
                          <TableCell className="cursor-pointer" onClick={(e) => { e.stopPropagation(); togglePeSelect(entry.id); }}><Checkbox checked={selectedPeIds.includes(entry.id)} className="pointer-events-none" /></TableCell>
                          <TableCell className="text-sm">{formatDate(entry.date)}</TableCell>
                          <TableCell className="font-medium text-sm">{entry.supplier_name}</TableCell>
                          <TableCell className="hidden sm:table-cell">{productDisplay}</TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            <div>{(entry.quantity_kg || 0).toLocaleString()} KG</div>
                            <div className="text-xs text-muted-foreground">{toTons(entry.quantity_kg)}</div>
                          </TableCell>
                          <TableCell className="text-right font-mono hidden sm:table-cell">{formatCurrency(entry.amount || 0)}</TableCell>
                          <TableCell className="text-right font-mono hidden sm:table-cell">{formatCurrency(entry.grand_total || entry.amount || 0)}</TableCell>
                          <TableCell className="hidden sm:table-cell text-sm">{entry.invoice_number || '-'}</TableCell>
                          <TableCell className="hidden sm:table-cell text-sm">{entry.vehicle_number || '-'}</TableCell>
                          <TableCell className="hidden sm:table-cell text-sm">{brandDisplay}</TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Print PDF" onClick={(e) => { e.stopPropagation(); printSingle(`/exports/purchase-entries/${entry.id}/pdf`, entry.invoice_number || 'bill'); }} data-testid={`print-pe-${entry.id}`}><Printer className="h-3 w-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditPE(entry)} data-testid={`edit-pe-${entry.id}`}><Pencil className="h-3 w-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeletePE(entry)} data-testid={`delete-pe-${entry.id}`}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {expandedMobileId === entry.id && (
                          <TableRow className="sm:hidden bg-muted/20">
                            <TableCell colSpan={11}>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm py-1">
                                {li.map((item, idx) => (
                                  <React.Fragment key={idx}>
                                    <span className="text-muted-foreground">Item {idx + 1}</span>
                                    <span className="text-right">{item.product_name} {item.brand ? `[${item.brand}]` : ''}</span>
                                    <span className="text-muted-foreground pl-2">Qty/Rate</span>
                                    <span className="text-right font-mono">{item.quantity_kg?.toLocaleString()} KG @ {formatCurrency(item.rate)}</span>
                                  </React.Fragment>
                                ))}
                                <span className="text-muted-foreground">Amount</span><span className="text-right font-mono">{formatCurrency(entry.grand_total || entry.amount || 0)}</span>
                                <span className="text-muted-foreground">Invoice</span><span className="text-right">{entry.invoice_number || '-'}</span>
                                <span className="text-muted-foreground">Vehicle</span><span className="text-right">{entry.vehicle_number || '-'}</span>
                                {entry.remarks && <><span className="text-muted-foreground">Remarks</span><span className="text-right">{entry.remarks}</span></>}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    )})}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

        {/* ========== OUTSTANDING TAB ========== */}
      {activeTab === 'outstanding' && (
        <div className="space-y-4">
          {outstandingLoading ? <Skeleton className="h-64 w-full" /> : outstanding && (
            <>
              {/* Product-wise Summary */}
              {outstanding.products && outstanding.products.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base">Product-wise Summary</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs sm:text-sm">Product</TableHead>
                          <TableHead className="text-right text-xs sm:text-sm">Ordered</TableHead>
                          <TableHead className="text-right text-xs sm:text-sm text-income">Received</TableHead>
                          <TableHead className="text-right text-xs sm:text-sm text-expense">Pending</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {outstanding.products.map(p => (
                          <React.Fragment key={p.product_id}>
                            <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => setExpandedMobileId(expandedMobileId === `prod-${p.product_id}` ? null : `prod-${p.product_id}`)}>
                              <TableCell className="text-sm font-medium">{p.product_name}</TableCell>
                              <TableCell className="text-right font-mono text-sm">{toTons(p.total_ordered_kg)}</TableCell>
                              <TableCell className="text-right font-mono text-sm text-income">{toTons(p.total_received_kg)}</TableCell>
                              <TableCell className="text-right font-mono text-sm text-expense font-semibold">{toTons(p.total_remaining_kg)}</TableCell>
                            </TableRow>
                            {expandedMobileId === `prod-${p.product_id}` && p.orders && p.orders.length > 0 && (
                              <TableRow className="bg-muted/10">
                                <TableCell colSpan={4} className="p-0">
                                  {/* Desktop sub-table */}
                                  <div className="hidden sm:block px-4 py-2">
                                    <Table>
                                      <TableHeader><TableRow>
                                        <TableHead className="text-xs">PO #</TableHead>
                                        <TableHead className="text-xs">Brand</TableHead>
                                        <TableHead className="text-xs">Supplier Group</TableHead>
                                        <TableHead className="text-right text-xs">Ordered</TableHead>
                                        <TableHead className="text-right text-xs">Received</TableHead>
                                        <TableHead className="text-right text-xs text-expense">Pending</TableHead>
                                        <TableHead className="text-right text-xs">Rate</TableHead>
                                      </TableRow></TableHeader>
                                      <TableBody>
                                        {p.orders.map((o, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell className="text-xs font-mono">{o.po_number}</TableCell>
                                            <TableCell className="text-xs">{o.brand || '-'}</TableCell>
                                            <TableCell className="text-xs">{o.supplier_group_name}</TableCell>
                                            <TableCell className="text-right text-xs font-mono">{toTons(o.quantity_kg)}</TableCell>
                                            <TableCell className="text-right text-xs font-mono text-income">{toTons(o.received_kg)}</TableCell>
                                            <TableCell className="text-right text-xs font-mono text-expense">{toTons(o.pending_kg)}</TableCell>
                                            <TableCell className="text-right text-xs font-mono">{formatCurrency(o.rate)}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                  {/* Mobile sub-cards */}
                                  <div className="sm:hidden px-3 py-2 space-y-2">
                                    {p.orders.map((o, idx) => (
                                      <div key={idx} className="bg-background border rounded px-2.5 py-2 text-xs space-y-1">
                                        <div className="flex items-center justify-between">
                                          <span className="font-mono font-semibold">{o.po_number}</span>
                                          <span className="font-mono">{formatCurrency(o.rate)}</span>
                                        </div>
                                        <div className="text-muted-foreground">{o.supplier_group_name} {o.brand ? `· ${o.brand}` : ''}</div>
                                        <div className="flex justify-between">
                                          <span className="font-mono">{toTons(o.quantity_kg)}</span>
                                          <span className="font-mono text-income">{toTons(o.received_kg)}</span>
                                          <span className="font-mono text-expense">{toTons(o.pending_kg)}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {outstanding.groups.map(group => (
                <Card key={group.group_id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Building2 className="h-4 w-4" />{group.group_name}
                      <Badge variant="outline" className="ml-auto">{group.orders.length} POs</Badge>
                    </CardTitle>
                    <div className="grid grid-cols-3 gap-1 sm:gap-2 mt-2">
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground">Ordered</p>
                        <p className="font-mono font-semibold text-sm">{toTons(group.total_ordered_kg)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground">Received</p>
                        <p className="font-mono font-semibold text-income text-sm">{toTons(group.total_received_kg)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground">Remaining</p>
                        <p className="font-mono font-semibold text-expense text-sm">{toTons(group.total_remaining_kg)}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="px-0 pb-0 pt-4">
                    {/* Desktop table view */}
                    <div className="hidden sm:block">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-sm">PO #</TableHead>
                            <TableHead className="text-sm">Date</TableHead>
                            <TableHead>Product</TableHead>
                            <TableHead>Brand</TableHead>
                            <TableHead className="text-right text-sm">Ordered</TableHead>
                            <TableHead className="text-right text-sm">Rate</TableHead>
                            <TableHead className="text-right text-sm text-income">Received</TableHead>
                            <TableHead className="text-right text-sm text-expense">Remaining</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.orders.map(o => (
                            <React.Fragment key={o.id}>
                              <TableRow
                                className={`cursor-pointer hover:bg-muted/50 ${expandedMobileId === `os-${o.id}` ? 'bg-muted/30' : ''}`}
                                onClick={() => setExpandedMobileId(expandedMobileId === `os-${o.id}` ? null : `os-${o.id}`)}
                                data-testid={`outstanding-po-${o.id}`}
                              >
                                <TableCell className="font-mono text-xs">{o.po_number || '-'}</TableCell>
                                <TableCell className="text-sm">{formatDate(o.date)}</TableCell>
                                <TableCell>{o.product_name}</TableCell>
                                <TableCell className="text-sm">{o.brand || '-'}</TableCell>
                                <TableCell className="text-right font-mono text-sm">{toTons(o.quantity_kg)}</TableCell>
                                <TableCell className="text-right font-mono text-sm">{formatCurrency(o.rate || 0)}</TableCell>
                                <TableCell className="text-right font-mono text-sm text-income">{toTons(o.received_kg)}</TableCell>
                                <TableCell className="text-right font-mono text-sm text-expense">{toTons(o.remaining_kg)}</TableCell>
                              </TableRow>
                              {expandedMobileId === `os-${o.id}` && (
                                <TableRow className="bg-muted/10">
                                  <TableCell colSpan={8} className="p-0">
                                    <div className="px-4 py-3 space-y-3">
                                      <div className="flex flex-wrap items-center gap-3 text-xs">
                                        <Badge variant="outline" className="capitalize">{o.status}</Badge>
                                        {o.credit_days && <span className="text-muted-foreground">Credit: <span className="font-mono font-medium text-foreground">{o.credit_days} days</span></span>}
                                      </div>
                                      {o.remarks && <div className="text-xs text-muted-foreground bg-background border rounded px-2 py-1.5"><span className="font-medium text-foreground">Remarks:</span> {o.remarks}</div>}
                                      {o.firm_entries && o.firm_entries.length > 0 ? (
                                        <div className="rounded border overflow-hidden">
                                          <Table>
                                            <TableHeader><TableRow>
                                              <TableHead className="text-xs py-1.5">Firm</TableHead>
                                              <TableHead className="text-xs py-1.5">Invoice</TableHead>
                                              <TableHead className="text-xs py-1.5">Date</TableHead>
                                              <TableHead className="text-xs py-1.5">Vehicle</TableHead>
                                              <TableHead className="text-xs py-1.5">Brand</TableHead>
                                              <TableHead className="text-right text-xs py-1.5">Qty</TableHead>
                                              <TableHead className="text-right text-xs py-1.5">Rate</TableHead>
                                            </TableRow></TableHeader>
                                            <TableBody>
                                              {o.firm_entries.flatMap((fe) =>
                                                fe.entries.map((e, eIdx) => (
                                                  <TableRow key={`${fe.firm_name}-${eIdx}`}>
                                                    <TableCell className="text-xs py-1.5 font-medium">{fe.firm_name}</TableCell>
                                                    <TableCell className="text-xs py-1.5 font-mono">{e.invoice_number || '-'}</TableCell>
                                                    <TableCell className="text-xs py-1.5">{formatDate(e.date)}</TableCell>
                                                    <TableCell className="text-xs py-1.5">{e.vehicle_number || '-'}</TableCell>
                                                    <TableCell className="text-xs py-1.5">{e.brand || '-'}</TableCell>
                                                    <TableCell className="text-right text-xs py-1.5 font-mono">{toTons(e.quantity_kg)}</TableCell>
                                                    <TableCell className="text-right text-xs py-1.5 font-mono">{formatCurrency(e.rate)}</TableCell>
                                                  </TableRow>
                                                ))
                                              )}
                                            </TableBody>
                                          </Table>
                                        </div>
                                      ) : (
                                        <p className="text-xs text-muted-foreground text-center py-2">No purchase entries yet</p>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Mobile card view */}
                    <div className="sm:hidden divide-y">
                      {group.orders.map(o => (
                        <div key={o.id} data-testid={`outstanding-po-m-${o.id}`}>
                          <div
                            className={`px-3 py-2.5 cursor-pointer active:bg-muted/50 ${expandedMobileId === `os-${o.id}` ? 'bg-muted/30' : ''}`}
                            onClick={() => setExpandedMobileId(expandedMobileId === `os-${o.id}` ? null : `os-${o.id}`)}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-mono text-xs font-semibold">{o.po_number}</span>
                              <span className="text-xs text-muted-foreground">{formatDate(o.date)}</span>
                            </div>
                            <div className="text-sm">{o.product_name} {o.brand ? <span className="text-muted-foreground">({o.brand})</span> : ''}</div>
                            <div className="flex items-center justify-between mt-1.5 text-xs">
                              <span className="font-mono">{toTons(o.quantity_kg)} @ {formatCurrency(o.rate || 0)}</span>
                              <div className="flex gap-3">
                                <span className="font-mono text-income">{toTons(o.received_kg)}</span>
                                <span className="font-mono text-expense">{toTons(o.remaining_kg)}</span>
                              </div>
                            </div>
                          </div>
                          {expandedMobileId === `os-${o.id}` && (
                            <div className="bg-muted/10 px-3 py-2.5 space-y-2 border-t">
                              <div className="flex flex-wrap items-center gap-2 text-xs">
                                <Badge variant="outline" className="capitalize text-[10px]">{o.status}</Badge>
                                {o.credit_days && <span className="text-muted-foreground">Credit: <span className="font-mono font-medium text-foreground">{o.credit_days} days</span></span>}
                              </div>
                              {o.remarks && <div className="text-xs text-muted-foreground bg-background border rounded px-2 py-1.5 break-words"><span className="font-medium text-foreground">Remarks:</span> {o.remarks}</div>}
                              {o.firm_entries && o.firm_entries.length > 0 ? (
                                <div className="space-y-2">
                                  {o.firm_entries.flatMap((fe) =>
                                    fe.entries.map((e, eIdx) => (
                                      <div key={`${fe.firm_name}-${eIdx}`} className="bg-background border rounded px-2.5 py-2 text-xs space-y-1">
                                        <div className="flex items-center justify-between">
                                          <span className="font-semibold">{fe.firm_name}</span>
                                          <span className="font-mono">{formatDate(e.date)}</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-muted-foreground">
                                          <span>Invoice</span><span className="text-right text-foreground font-mono">{e.invoice_number || '-'}</span>
                                          <span>Vehicle</span><span className="text-right text-foreground">{e.vehicle_number || '-'}</span>
                                          <span>Brand</span><span className="text-right text-foreground">{e.brand || '-'}</span>
                                          <span>Qty</span><span className="text-right text-foreground font-mono">{toTons(e.quantity_kg)}</span>
                                          <span>Rate</span><span className="text-right text-foreground font-mono">{formatCurrency(e.rate)}</span>
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground text-center py-2">No purchase entries yet</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {outstanding.groups.length === 0 && <p className="text-center text-muted-foreground py-8">No outstanding purchase orders</p>}
            </>
          )}
        </div>
      )}

      {/* ========== PURCHASE OUTSTANDING TAB ========== */}
      {activeTab === 'payment-outstanding' && (
        <div className="space-y-4">
          {paymentOutstandingLoading ? <Skeleton className="h-64 w-full" /> : paymentOutstanding && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Total Bill</CardTitle></CardHeader><CardContent className="px-3 pb-3"><p className="text-base sm:text-lg font-bold font-mono">{formatCurrency(paymentOutstanding.total_amount)}</p></CardContent></Card>
                <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Paid</CardTitle></CardHeader><CardContent className="px-3 pb-3"><p className="text-base sm:text-lg font-bold font-mono text-income">{formatCurrency(paymentOutstanding.total_paid)}</p></CardContent></Card>
                <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Outstanding</CardTitle></CardHeader><CardContent className="px-3 pb-3"><p className="text-base sm:text-lg font-bold font-mono text-expense">{formatCurrency(paymentOutstanding.total_outstanding)}</p></CardContent></Card>
                <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Overdue</CardTitle></CardHeader><CardContent className="px-3 pb-3"><p className="text-base sm:text-lg font-bold font-mono text-destructive">{formatCurrency(paymentOutstanding.total_overdue)}</p></CardContent></Card>
              </div>
              {paymentOutstanding.firms.map(firm => (
                <Card key={firm.supplier_id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Building2 className="h-4 w-4" />{firm.supplier_name}
                      <span className="text-xs text-muted-foreground font-normal">({firm.group_name})</span>
                    </CardTitle>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 sm:gap-2 mt-2">
                      <div className="text-center"><p className="text-xs text-muted-foreground">Bill</p><p className="font-mono font-semibold text-sm">{formatCurrency(firm.total_amount)}</p></div>
                      <div className="text-center"><p className="text-xs text-muted-foreground">Paid</p><p className="font-mono font-semibold text-income text-sm">{formatCurrency(firm.total_paid)}</p></div>
                      <div className="text-center"><p className="text-xs text-muted-foreground">Outstanding</p><p className="font-mono font-semibold text-expense text-sm">{formatCurrency(firm.total_outstanding)}</p></div>
                      <div className="text-center"><p className="text-xs text-muted-foreground">Overdue</p><p className="font-mono font-semibold text-destructive text-sm">{formatCurrency(firm.total_overdue)}</p></div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs sm:text-sm w-[80px]">Invoice</TableHead>
                            <TableHead className="text-xs sm:text-sm w-[100px]">Date</TableHead>
                            <TableHead className="hidden sm:table-cell text-xs w-[110px]">Due Date</TableHead>
                            <TableHead className="text-xs sm:text-sm text-center w-[70px]">Status</TableHead>
                            <TableHead className="text-right text-xs sm:text-sm w-[110px]">Bill</TableHead>
                            <TableHead className="text-right text-xs sm:text-sm text-income hidden sm:table-cell w-[100px]">Paid</TableHead>
                            <TableHead className="text-right text-xs sm:text-sm text-expense w-[110px]">Due</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {firm.entries.map(entry => (
                            <React.Fragment key={entry.id}>
                              <TableRow
                                className={`cursor-pointer hover:bg-muted/50 ${entry.is_overdue ? 'bg-destructive/5' : ''} ${expandedMobileId === `po-${entry.id}` ? 'bg-muted/30' : ''}`}
                                onClick={() => setExpandedMobileId(expandedMobileId === `po-${entry.id}` ? null : `po-${entry.id}`)}
                                data-testid={`payment-outstanding-${entry.id}`}
                              >
                                <TableCell className="text-sm font-medium">{entry.invoice_number || '-'}</TableCell>
                                <TableCell className="text-sm whitespace-nowrap">{formatDate(entry.date)}</TableCell>
                                <TableCell className="hidden sm:table-cell text-sm whitespace-nowrap">
                                  <div>{formatDate(entry.due_date)}</div>
                                  {entry.is_overdue ? <span className="text-[10px] text-destructive font-semibold">{entry.overdue_days}d overdue</span> : <span className="text-[10px] text-muted-foreground">{entry.remaining_days}d remaining</span>}
                                </TableCell>
                                <TableCell className="text-center">
                                  {entry.is_overdue ? <Badge variant="destructive" className="text-[10px]">Overdue</Badge> : <Badge variant="outline" className="text-[10px]">{entry.remaining_days}d</Badge>}
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm whitespace-nowrap">{formatCurrency(entry.grand_total)}</TableCell>
                                <TableCell className="text-right font-mono text-sm text-income hidden sm:table-cell whitespace-nowrap">{formatCurrency(entry.paid_amount)}</TableCell>
                                <TableCell className="text-right font-mono text-sm text-expense font-semibold whitespace-nowrap">{formatCurrency(entry.outstanding)}</TableCell>
                              </TableRow>
                              {expandedMobileId === `po-${entry.id}` && (
                                <TableRow className="sm:hidden bg-muted/20">
                                  <TableCell colSpan={7} className="py-2 px-4">
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                      <span className="text-muted-foreground">Product</span><span className="text-right">{entry.product_name}{entry.item_count > 1 ? ` +${entry.item_count - 1}` : ''}</span>
                                      <span className="text-muted-foreground">Due Date</span><span className="text-right">{formatDate(entry.due_date)}</span>
                                      <span className="text-muted-foreground">Days</span><span className={`text-right font-semibold ${entry.is_overdue ? 'text-destructive' : ''}`}>{entry.is_overdue ? `${entry.overdue_days}d overdue` : `${entry.remaining_days}d remaining`}</span>
                                      <span className="text-muted-foreground">Paid</span><span className="text-right font-mono text-income">{formatCurrency(entry.paid_amount)}</span>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {paymentOutstanding.firms.length === 0 && <p className="text-center text-muted-foreground py-8">No outstanding payments</p>}
            </>
          )}
        </div>
      )}

      {/* ========== PAYMENTS TAB ========== */}
      {activeTab === 'payments' && (
        <div className="space-y-4">
          <DateRangeFilter startDate={paymentDateRange.start_date} endDate={paymentDateRange.end_date}
            onStartDateChange={(v) => setPaymentDateRange(prev => ({...prev, start_date: v}))}
            onEndDateChange={(v) => setPaymentDateRange(prev => ({...prev, end_date: v}))}
            onApply={(s, e) => { setPaymentDateRange({start_date: s, end_date: e}); fetchPayments(s, e); }}
            activeQuick={paymentQuick} onQuickSelect={setPaymentQuick} />
          {paymentsLoading ? <Skeleton className="h-64 w-full" /> : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10 cursor-pointer" onClick={toggleAllPay}><Checkbox checked={allPaySelected} className="pointer-events-none" data-testid="select-all-payments" /></TableHead>
                        <TableHead className="text-xs sm:text-sm">Date</TableHead>
                        <TableHead className="text-xs sm:text-sm">Supplier</TableHead>
                        <TableHead className="text-xs sm:text-sm">Mode</TableHead>
                        <TableHead className="hidden sm:table-cell">Reference</TableHead>
                        <TableHead className="hidden sm:table-cell">Invoices</TableHead>
                        <TableHead className="text-right text-xs sm:text-sm">Amount</TableHead>
                        <TableHead className="text-xs sm:text-sm">Status</TableHead>
                        <TableHead className="text-right text-xs sm:text-sm hidden sm:table-cell">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.length === 0 ? (
                        <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No payments recorded</TableCell></TableRow>
                      ) : payments.map(p => (
                        <React.Fragment key={p.id}>
                          <TableRow
                            className={`cursor-pointer hover:bg-muted/50 ${selectedPayIds.includes(p.id) ? 'bg-muted/50' : ''} ${expandedMobileId === `pmt-${p.id}` ? 'bg-muted/30' : ''}`}
                            onClick={() => setExpandedMobileId(expandedMobileId === `pmt-${p.id}` ? null : `pmt-${p.id}`)}
                            data-testid={`payment-row-${p.id}`}
                          >
                            <TableCell className="cursor-pointer" onClick={(e) => { e.stopPropagation(); togglePaySelect(p.id); }}><Checkbox checked={selectedPayIds.includes(p.id)} className="pointer-events-none" /></TableCell>
                            <TableCell className="text-sm whitespace-nowrap">{formatDate(p.date)}</TableCell>
                            <TableCell className="text-sm">{p.supplier_name}</TableCell>
                            <TableCell><Badge variant="outline" className="text-[10px] capitalize">{p.mode?.replace('_', ' ')}</Badge></TableCell>
                            <TableCell className="hidden sm:table-cell text-sm font-mono">{p.reference || '-'}</TableCell>
                            <TableCell className="hidden sm:table-cell text-xs">{(p.allocations || []).map(a => a.invoice_number || a.purchase_entry_id?.slice(-4)).join(', ')}</TableCell>
                            <TableCell className="text-right font-mono text-sm whitespace-nowrap">{formatCurrency(p.amount)}</TableCell>
                            <TableCell>
                              <Badge variant={p.status === 'completed' || p.status === 'cleared' ? 'default' : p.status === 'bounced' ? 'destructive' : 'secondary'} className="text-[10px]">
                                {p.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right hidden sm:table-cell">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="icon" className="h-7 w-7" title="Print PDF" onClick={(e) => { e.stopPropagation(); printSingle(`/exports/payments/${p.id}/pdf`, p.payment_number || 'payment'); }} data-testid={`print-pmt-${p.id}`}><Printer className="h-3.5 w-3.5" /></Button>
                                {p.status === 'pending' && (
                                  <>
                                    <Button variant="ghost" size="sm" className="h-7 text-xs text-income" onClick={(e) => { e.stopPropagation(); setClearingPayment(p); setClearDate(getToday()); setClearDialogOpen(true); }} data-testid={`clear-pmt-${p.id}`}>Clear</Button>
                                    <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive" onClick={(e) => { e.stopPropagation(); handleBouncePayment(p); }} data-testid={`bounce-pmt-${p.id}`}>Bounce</Button>
                                  </>
                                )}
                                {(p.status === 'pending' || p.status === 'bounced') && (
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={(e) => { e.stopPropagation(); handleDeletePayment(p); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                          {expandedMobileId === `pmt-${p.id}` && (
                            <TableRow className="sm:hidden bg-muted/20">
                              <TableCell colSpan={9} className="py-2 px-4">
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                  <span className="text-muted-foreground">Reference</span><span className="text-right font-mono">{p.reference || '-'}</span>
                                  <span className="text-muted-foreground">Bank</span><span className="text-right">{p.bank_name || '-'}</span>
                                  <span className="text-muted-foreground">Invoices</span><span className="text-right">{(p.allocations || []).map(a => a.invoice_number || '?').join(', ')}</span>
                                  {p.clear_date && <><span className="text-muted-foreground">Clear Date</span><span className="text-right">{formatDate(p.clear_date)}</span></>}
                                  {p.remarks && <><span className="text-muted-foreground">Remarks</span><span className="text-right">{p.remarks}</span></>}
                                </div>
                                {p.status === 'pending' && (
                                  <div className="flex gap-2 mt-2 pt-2 border-t border-border/40">
                                    <Button variant="default" size="sm" className="h-7 text-xs flex-1" onClick={(e) => { e.stopPropagation(); setClearingPayment(p); setClearDate(getToday()); setClearDialogOpen(true); }}>Clear</Button>
                                    <Button variant="destructive" size="sm" className="h-7 text-xs flex-1" onClick={(e) => { e.stopPropagation(); handleBouncePayment(p); }}>Bounce</Button>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Unified Payment Dialog */}
      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handlePaySubmit} className="space-y-4">
            {payError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{payError}</AlertDescription></Alert>}

            <div className="space-y-2">
              <Label>Supplier</Label>
              <SearchableSelect
                options={payFirmOptions}
                value={payForm.supplier_id}
                onChange={(v) => selectPayFirm(v)}
                placeholder="Select supplier..."
                data-testid="pay-supplier"
              />
            </div>

            <div className="space-y-2">
              <Label>Payment Mode</Label>
              <SearchableSelect
                options={modeOptions}
                value={payForm.mode}
                onChange={(v) => setPayForm(prev => ({...prev, mode: v}))}
                placeholder="Select mode..."
                data-testid="pay-mode"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input type="number" step="0.01" value={payForm.amount} onChange={(e) => updatePayAmount(e.target.value)} placeholder="0.00" data-testid="pay-amount" />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <DateInput fyStartYear={selectedFY} value={payForm.date} onChange={(e) => setPayForm(prev => ({...prev, date: e.target.value}))} required data-testid="pay-date" />
              </div>
            </div>

            {(payForm.mode === 'cheque' || payForm.mode === 'online' || payForm.mode === 'bank_transfer') && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{payForm.mode === 'cheque' ? 'Cheque Number' : 'Reference / UTR'}</Label>
                  <Input value={payForm.reference} onChange={(e) => setPayForm(prev => ({...prev, reference: e.target.value}))} placeholder={payForm.mode === 'cheque' ? 'Cheque #' : 'UTR / Ref #'} data-testid="pay-reference" />
                </div>
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Input value={payForm.bank_name} onChange={(e) => setPayForm(prev => ({...prev, bank_name: e.target.value}))} placeholder="e.g. SBI, HDFC" data-testid="pay-bank" />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Remarks</Label>
              <textarea className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[56px] resize-y" value={payForm.remarks} onChange={(e) => setPayForm(prev => ({...prev, remarks: e.target.value}))} placeholder="Optional remarks..." data-testid="pay-remarks" />
            </div>

            {/* Invoice allocation - shown when supplier selected */}
            {payForm.allocations.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Invoice Allocation (FIFO auto-adjusted)</Label>
                <div className="border rounded-md divide-y">
                  {payForm.allocations.map((alloc, idx) => (
                    <div key={alloc.purchase_entry_id} className="grid grid-cols-[1fr_auto_100px] items-center gap-2 px-3 py-2">
                      <div>
                        <span className="text-sm font-medium">{alloc.invoice_number}</span>
                        <span className="text-xs text-muted-foreground ml-2">Due: {alloc.due_date ? formatDate(alloc.due_date) : '-'}</span>
                      </div>
                      <span className="text-sm font-mono text-expense">{formatCurrency(alloc.outstanding)}</span>
                      <Input
                        type="number" step="0.01" className="h-8 text-sm text-right"
                        value={alloc.amount} onChange={(e) => updateAllocation(idx, e.target.value)}
                        placeholder="0"
                        data-testid={`alloc-${idx}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {payForm.mode === 'cheque' && (
              <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/30 p-2 rounded">Cheque payments stay pending until marked "Cleared".</p>
            )}

            <DialogFooter>
              <FormShortcutHints />
              <Button type="button" variant="outline" onClick={() => setPayDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={paySubmitting} data-testid="pay-submit">
                {paySubmitting ? 'Recording...' : `${payForm.mode === 'cheque' ? 'Record Cheque' : 'Record Payment'} (${formatCurrency(parseFloat(payForm.amount) || 0)})`}
              </Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* Clear Cheque Dialog */}
      <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Clear Cheque</DialogTitle></DialogHeader>
          {clearingPayment && (
            <div className="space-y-4">
              <div className="p-3 rounded-md bg-muted space-y-1">
                <p className="text-sm"><span className="text-muted-foreground">Cheque #:</span> <span className="font-mono font-medium">{clearingPayment.reference}</span></p>
                <p className="text-sm"><span className="text-muted-foreground">Amount:</span> <span className="font-mono font-medium">{formatCurrency(clearingPayment.amount)}</span></p>
                <p className="text-sm"><span className="text-muted-foreground">Supplier:</span> {clearingPayment.supplier_name}</p>
              </div>
              <div className="space-y-2">
                <Label>Payment Clear Date</Label>
                <DateInput fyStartYear={selectedFY} value={clearDate} onChange={(e) => setClearDate(e.target.value)} required data-testid="clear-date-input" />
                <p className="text-xs text-muted-foreground">This date will be used in the payment ledger entry.</p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setClearDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleClearPayment} disabled={clearSubmitting} data-testid="clear-confirm-btn">{clearSubmitting ? 'Clearing...' : 'Clear Cheque'}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

        {/* ========== SUPPLIERS TAB ========== */}
      {activeTab === 'suppliers' && (
        <div className="space-y-4">
          {/* Supplier Groups */}
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide" data-testid="supplier-groups-list">
            <span className="text-sm font-medium text-muted-foreground mr-1 shrink-0">Groups:</span>
            {supplierGroups.map(g => (
              <div key={g.id} className="group inline-flex items-center gap-1 border rounded-md px-2 py-1 bg-card hover:border-primary/40 transition-colors" data-testid={`group-${g.id}`}>
                {editingGroupId === g.id ? (
                  <div className="flex items-center gap-1">
                    <Input
                      autoFocus
                      value={editGroupName}
                      onChange={(e) => setEditGroupName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSaveGroupEdit(g.id); } if (e.key === 'Escape') setEditingGroupId(null); }}
                      className="h-6 w-24 text-xs px-1.5"
                      placeholder="Name"
                      data-testid={`group-edit-input-${g.id}`}
                    />
                    <Input
                      type="number"
                      value={editGroupCreditDays}
                      onChange={(e) => setEditGroupCreditDays(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSaveGroupEdit(g.id); } if (e.key === 'Escape') setEditingGroupId(null); }}
                      onBlur={() => handleSaveGroupEdit(g.id)}
                      className="h-6 w-14 text-xs px-1.5"
                      placeholder="Days"
                    />
                  </div>
                ) : (
                  <>
                    <span className="text-sm">{g.name}</span>
                    <span className="text-xs text-muted-foreground">({g.credit_days || 30}d)</span>
                    <button onClick={() => { setEditingGroupId(g.id); setEditGroupName(g.name); setEditGroupCreditDays(String(g.credit_days || 30)); }} className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:text-primary" data-testid={`group-edit-${g.id}`}>
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button onClick={() => handleDeleteGroup(g)} className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:text-destructive" data-testid={`group-delete-${g.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </>
                )}
              </div>
            ))}
            {supplierGroups.length === 0 && <span className="text-sm text-muted-foreground italic">No groups yet — create one when adding a firm</span>}
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 cursor-pointer" onClick={toggleAllFirms}><Checkbox checked={allFirmsSelected} className="pointer-events-none" data-testid="select-all-firms" /></TableHead>
                      <TableHead><SortableHeader label="Firm Name" sortKey="company_name" currentSortKey={firmSortKey} sortDirection={firmSortDir} onSort={handleFirmSort} /></TableHead>
                      <TableHead><SortableHeader label="Group" sortKey="group_name" currentSortKey={firmSortKey} sortDirection={firmSortDir} onSort={handleFirmSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="Email" sortKey="company_email" currentSortKey={firmSortKey} sortDirection={firmSortDir} onSort={handleFirmSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="Credit Days" sortKey="credit_days" currentSortKey={firmSortKey} sortDirection={firmSortDir} onSort={handleFirmSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell">Contact</TableHead>
                      <TableHead className="w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedFirms.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No suppliers yet</TableCell></TableRow>
                    ) : sortedFirms.map(firm => (
                      <React.Fragment key={firm.id}>
                        <TableRow
                          className={`cursor-pointer sm:cursor-default ${selectedFirmIds.includes(firm.id) ? 'bg-muted/50' : ''} ${expandedMobileId === `f-${firm.id}` ? 'bg-muted/30' : ''}`}
                          onClick={() => setExpandedMobileId(expandedMobileId === `f-${firm.id}` ? null : `f-${firm.id}`)}
                          data-testid={`firm-row-${firm.id}`}
                        >
                          <TableCell className="cursor-pointer" onClick={(e) => { e.stopPropagation(); toggleFirmSelect(firm.id); }}><Checkbox checked={selectedFirmIds.includes(firm.id)} className="pointer-events-none" /></TableCell>
                          <TableCell className="font-medium">{firm.company_name}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{firm.group_name}</Badge></TableCell>
                          <TableCell className="hidden sm:table-cell">{firm.company_email || '-'}</TableCell>
                          <TableCell className="hidden sm:table-cell">{firm.credit_days}d</TableCell>
                          <TableCell className="hidden sm:table-cell text-sm">{firm.contact_persons?.[0]?.name || '-'}</TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditFirm(firm)}><Pencil className="h-3 w-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteFirm(firm)}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {expandedMobileId === `f-${firm.id}` && (
                          <TableRow className="sm:hidden bg-muted/20">
                            <TableCell colSpan={7}>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm py-1">
                                <span className="text-muted-foreground">Email</span><span className="text-right">{firm.company_email || '-'}</span>
                                <span className="text-muted-foreground">Credit Days</span><span className="text-right">{firm.credit_days}d</span>
                                {firm.contact_persons?.map((cp, i) => cp.name && (
                                  <React.Fragment key={i}>
                                    <span className="text-muted-foreground">Contact {i + 1}</span>
                                    <span className="text-right">{cp.name} {cp.mobile_number ? `(${cp.mobile_number})` : ''}</span>
                                  </React.Fragment>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== Bulk Action Bar ===== */}
      {(() => {
        const count = activeTab === 'purchase-orders' ? selectedPoIds.length : activeTab === 'purchase-entries' ? selectedPeIds.length : activeTab === 'suppliers' ? selectedFirmIds.length : 0;
        const clearFn = () => { setSelectedPoIds([]); setSelectedPeIds([]); setSelectedFirmIds([]); };
        const deleteFn = activeTab === 'purchase-orders' ? handleBulkDeletePOs : activeTab === 'purchase-entries' ? handleBulkDeletePEs : handleBulkDeleteFirms;
        if (count === 0) return null;
        return (
          <div className="fixed bottom-14 sm:bottom-14 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-5 py-3 rounded-full shadow-lg border bg-background" data-testid="bulk-action-bar">
            <span className="text-sm font-medium">{count} selected</span>
            <Button variant="outline" size="sm" onClick={clearFn}>Clear</Button>
            <Button variant="destructive" size="sm" onClick={deleteFn} data-testid="bulk-delete-btn"><Trash2 className="h-4 w-4 mr-1" />Delete selected</Button>
          </div>
        );
      })()}

      {/* ===== PO Dialog ===== */}
      <Dialog open={poDialogOpen} onOpenChange={setPoDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingPO ? 'Edit Purchase Order' : 'New Purchase Order'}</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleCreatePO} className="space-y-4">
            {poError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{poError}</AlertDescription></Alert>}
            <div className="grid grid-cols-[1fr_80px_140px] gap-3 items-end">
              <div className="space-y-2"><Label>Supplier Group</Label>
                <SearchableSelect options={groupOptions} value={poForm.supplier_group_id} onChange={handlePOGroupChange} onCreateNew={(name) => handleCreateGroupFromSelect(name, 'po')} placeholder="Select or create group" createNewLabel="Add group" data-testid="po-group-select" />
              </div>
              <div className="space-y-2"><Label>Credit Days</Label><Input type="number" value={poForm.credit_days} onChange={(e) => setPoForm(p => ({...p, credit_days: e.target.value}))} placeholder="30" data-testid="po-credit-days" /></div>
              <div className="space-y-2"><Label>Date</Label><DateInput fyStartYear={selectedFY} value={poForm.date} onChange={(e) => { const v = e.target.value; setPoForm(p => ({...p, date: v})); if (v && !isDateInFY(v, selectedFY)) toast.error(getFYDateError(selectedFY, fyLabel)); else reLookupAllHSNRates({...poForm, date: v}, setPoForm); }} required data-testid="po-date-input" /></div>
            </div>
            {/* Line Items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Line Items</Label>
                <div className="flex gap-1.5">
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={openNewProduct} title="New Product (Alt+P)" data-testid="po-new-product-btn"><Plus className="h-3 w-3 mr-1" />Product</Button>
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => addLineItem(setPoForm)} data-testid="po-add-line-btn"><Plus className="h-3 w-3 mr-1" />Item</Button>
                </div>
              </div>
              {poForm.line_items.map((li, idx) => {
                const prod = products.find(p => p.id === li.product_id);
                const brandOpts = prod?.brands?.filter(b => b)?.map(b => ({ value: b, label: b })) || [];
                const lineAmount = (parseFloat(li.quantity_kg) || 0) * (parseFloat(li.rate) || 0);
                const lineTax = (parseFloat(li.cgst_amount) || 0) + (parseFloat(li.sgst_amount) || 0) + (parseFloat(li.igst_amount) || 0);
                const lineTotal = lineAmount + lineTax;
                const brandSelect = brandOpts.length > 0 ? (
                  <SearchableSelect options={[{value: '', label: 'None'}, ...brandOpts]} value={li.brand} onChange={(v) => updateLineItem(poForm, setPoForm, idx, 'brand', v)} onCreateNew={async (name) => { if (!li.product_id) return; try { const p = products.find(pp => pp.id === li.product_id); await updateProduct(li.product_id, { brands: [...(p?.brands || []), name] }); const r = await getProducts(); setProducts(r.data); updateLineItem(poForm, setPoForm, idx, 'brand', name); toast.success(`Brand added`); } catch (err) { console.error('Brand add failed:', err); toast.error('Failed'); } }} placeholder="Brand" createNewLabel="Add brand" className="h-8 text-xs" data-testid={`po-line-brand-${idx}`} />
                ) : (
                  <Input value={li.brand} onChange={(e) => updateLineItem(poForm, setPoForm, idx, 'brand', e.target.value)} placeholder="Brand" className="h-8 text-xs" data-testid={`po-line-brand-${idx}`} />
                );
                return (
                  <div key={idx} className="border rounded-lg px-3 py-2 relative" data-testid={`po-line-item-${idx}`}>
                    {poForm.line_items.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" className="absolute top-1 right-1 h-5 w-5 text-destructive hover:text-destructive z-10" onClick={() => removeLineItem(setPoForm, idx)} data-testid={`po-remove-line-${idx}`}><Trash2 className="h-3 w-3" /></Button>
                    )}
                    {/* Desktop */}
                    <div className="hidden sm:block space-y-0.5">
                      <div className="grid grid-cols-[2.2fr_1.5fr_0.8fr_1.2fr_0.6fr_1fr_0.8fr_1fr] gap-2 items-end">
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">Product</Label>}
                          <SearchableSelect options={productOptions} value={li.product_id} onChange={(v) => { updateLineItem(poForm, setPoForm, idx, 'product_id', v); updateLineItem(poForm, setPoForm, idx, 'brand', ''); autoFillLineItemGST(poForm, setPoForm, idx, v); if (!editingPO) autoFillLineItemFromLastPO(poForm, setPoForm, idx, v); }} placeholder="Product" className="h-8 text-xs" data-testid={`po-line-product-${idx}`} />
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">Qty (KG)</Label>}
                          <Input type="number" step="0.01" min="0" className="h-8 text-xs" value={li.quantity_kg} onChange={(e) => handleLineItemKgChange(poForm, setPoForm, idx, e.target.value)} data-testid={`po-line-qty-${idx}`} />
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">Rate</Label>}
                          <Input type="number" step="0.01" min="0" className="h-8 text-xs" value={li.rate} onChange={(e) => { updateLineItem(poForm, setPoForm, idx, 'rate', e.target.value); setTimeout(() => recalcLineItemGST(poForm, setPoForm, idx), 0); }} data-testid={`po-line-rate-${idx}`} />
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">Brand</Label>}
                          {brandSelect}
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">GST%</Label>}
                          <Input type="number" step="0.01" min="0" className="h-8 text-xs" value={li.gst_rate} onChange={(e) => { updateLineItem(poForm, setPoForm, idx, 'gst_rate', e.target.value); setTimeout(() => recalcLineItemGST(poForm, setPoForm, idx), 0); }} placeholder="%" data-testid={`po-line-gst-${idx}`} />
                        </div>
                        <div className="text-right">
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground block">Basic Amt</Label>}
                          <div className="h-8 flex items-center justify-end text-xs font-mono">{lineAmount > 0 ? formatCurrency(lineAmount) : '-'}</div>
                        </div>
                        <div className="text-right">
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground block">Tax</Label>}
                          <div className="h-8 flex items-center justify-end text-xs font-mono text-muted-foreground">{lineTax > 0 ? formatCurrency(lineTax) : '-'}</div>
                        </div>
                        <div className="text-right">
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground block">Total</Label>}
                          <div className="h-8 flex items-center justify-end text-xs font-mono font-semibold">{lineTotal > 0 ? formatCurrency(lineTotal) : '-'}</div>
                        </div>
                      </div>
                      {/* Sub-info: HSN below product, Alt Qty below Qty */}
                      <div className="grid grid-cols-[2.2fr_1.5fr_0.8fr_1.2fr_0.6fr_1fr_0.8fr_1fr] gap-2 text-[10px] text-muted-foreground font-mono">
                        <div>{li.hsn_code ? `HSN: ${li.hsn_code}` : ''}</div>
                        <div>{li.quantity_kg ? <><span className="float-right">{toTons(parseFloat(li.quantity_kg))}{li.quantity_bags ? ` / ${li.quantity_bags} Bags` : ''}</span></> : ''}</div>
                        <div></div><div></div><div></div><div></div><div></div><div></div>
                      </div>
                    </div>
                    {/* Mobile */}
                    <div className="sm:hidden space-y-2">
                      <div className="space-y-0.5">
                        <Label className="text-xs">Product</Label>
                        <SearchableSelect options={productOptions} value={li.product_id} onChange={(v) => { updateLineItem(poForm, setPoForm, idx, 'product_id', v); updateLineItem(poForm, setPoForm, idx, 'brand', ''); autoFillLineItemGST(poForm, setPoForm, idx, v); if (!editingPO) autoFillLineItemFromLastPO(poForm, setPoForm, idx, v); }} placeholder="Select product" data-testid={`po-line-product-m-${idx}`} />
                        {li.hsn_code && <div className="text-[10px] text-muted-foreground font-mono">HSN: {li.hsn_code}</div>}
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-0.5">
                          <Label className="text-xs">Qty (KG)</Label>
                          <Input type="number" step="0.01" min="0" value={li.quantity_kg} onChange={(e) => handleLineItemKgChange(poForm, setPoForm, idx, e.target.value)} />
                          {li.quantity_kg && <div className="text-[10px] text-muted-foreground font-mono text-right">{toTons(parseFloat(li.quantity_kg))}{li.quantity_bags ? ` / ${li.quantity_bags} B` : ''}</div>}
                        </div>
                        <div className="space-y-0.5"><Label className="text-xs">Rate</Label><Input type="number" step="0.01" min="0" value={li.rate} onChange={(e) => { updateLineItem(poForm, setPoForm, idx, 'rate', e.target.value); setTimeout(() => recalcLineItemGST(poForm, setPoForm, idx), 0); }} /></div>
                        <div className="space-y-0.5"><Label className="text-xs">GST %</Label><Input type="number" step="0.01" min="0" value={li.gst_rate} onChange={(e) => { updateLineItem(poForm, setPoForm, idx, 'gst_rate', e.target.value); setTimeout(() => recalcLineItemGST(poForm, setPoForm, idx), 0); }} /></div>
                      </div>
                      <div className="space-y-0.5"><Label className="text-xs">Brand</Label>{brandSelect}</div>
                      <div className="flex justify-between items-center text-xs pt-1 border-t">
                        <span className="text-muted-foreground">Basic: {lineAmount > 0 ? formatCurrency(lineAmount) : '-'}{lineTax > 0 ? ` + Tax: ${formatCurrency(lineTax)}` : ''}</span>
                        <span className="font-mono font-semibold">{lineTotal > 0 ? formatCurrency(lineTotal) : '-'}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Totals */}
            {(() => {
              const { subtotal, totalTax } = computeTotals(poForm.line_items, 0);
              const rawTotal = subtotal + totalTax;
              const autoRound = rawTotal > 0 ? Math.round(rawTotal) - rawTotal : 0;
              const roundOff = poForm.round_off !== '' ? parseFloat(poForm.round_off) || 0 : Math.round(autoRound * 100) / 100;
              const grand = rawTotal + roundOff;
              return (
                <div className="border-t pt-2 text-sm space-y-0.5">
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono w-28 text-right">{formatCurrency(subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span className="font-mono w-28 text-right">{formatCurrency(totalTax)}</span></div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground flex items-center gap-1">Round Off <Button type="button" variant="ghost" size="icon" className="h-4 w-4" onClick={() => setPoEditRoundOff(!poEditRoundOff)} data-testid="po-round-off-btn"><Pencil className="h-2.5 w-2.5" /></Button></span>
                    {poEditRoundOff ? (
                      <Input type="number" step="0.01" autoFocus className="w-28 h-7 text-xs text-right font-mono" value={poForm.round_off !== '' ? poForm.round_off : String(Math.round(autoRound * 100) / 100)} onChange={(e) => setPoForm(p => ({...p, round_off: e.target.value}))} onBlur={() => setPoEditRoundOff(false)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setPoEditRoundOff(false); } }} data-testid="po-round-off-input" />
                    ) : (
                      <span className="font-mono w-28 text-right">{formatCurrency(roundOff)}</span>
                    )}
                  </div>
                  <div className="flex justify-between font-semibold text-base border-t pt-1 mt-1"><span>Grand Total</span><span className="font-mono w-28 text-right">{formatCurrency(grand)}</span></div>
                </div>
              );
            })()}
            <div className="space-y-2"><Label>Remarks</Label><Textarea placeholder="Optional remarks... (Shift+Enter for new line)" rows={2} value={poForm.remarks} onChange={(e) => setPoForm(p => ({...p, remarks: e.target.value}))} data-testid="po-remarks" /></div>
            <DialogFooter>
              <FormShortcutHints shortcuts={[{key: 'Alt+P', label: 'New Product'}, {key: '⇧+Enter', label: 'New line'}]} />
              <Button type="button" variant="outline" onClick={() => setPoDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={poSubmitting || !poForm.supplier_group_id || !poForm.line_items.some(li => li.product_id)} data-testid="po-submit-btn">{poSubmitting ? 'Saving...' : editingPO ? 'Update' : 'Create PO'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* ===== PE Dialog ===== */}
      <Dialog open={peDialogOpen} onOpenChange={(v) => { setPeDialogOpen(v); if (!v) setEditingPE(null); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingPE ? 'Edit Purchase Entry' : 'New Purchase Entry'}</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleSubmitPE} className="space-y-3">
            {peError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{peError}</AlertDescription></Alert>}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2 sm:col-span-1"><Label>Purchase Order</Label>
                <SearchableSelect options={poOptions} value={peForm.purchase_order_id} onChange={handlePePoChange} placeholder="Select PO" data-testid="pe-po-select" />
              </div>
              <div className="space-y-2 col-span-2 sm:col-span-1"><Label>Supplier (Firm)</Label>
                <div className="flex gap-1.5">
                  <div className="flex-1"><SearchableSelect options={supplierOptions} value={peForm.supplier_id} onChange={(v) => { const firm = suppliers.find(f => f.id === v) || allFirms.find(f => f.id === v); setPeForm(p => ({...p, supplier_id: v, credit_days: firm ? String(firm.credit_days || 0) : p.credit_days })); }} placeholder="Select firm" data-testid="pe-supplier-select" /></div>
                  <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={openQuickFirm} title="New Supplier (Alt+S)" data-testid="pe-new-supplier-btn"><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
            {selectedPOForEntry && (
              <div className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1.5 font-mono">
                {selectedPOForEntry.po_number} | Ordered: {(selectedPOForEntry.quantity_kg || 0).toLocaleString()} KG | Received: {(selectedPOForEntry.received_kg || 0).toLocaleString()} KG | Remaining: {(selectedPOForEntry.remaining_kg || 0).toLocaleString()} KG
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Date</Label><DateInput fyStartYear={selectedFY} value={peForm.date} onChange={(e) => { const v = e.target.value; setPeForm(p => ({...p, date: v})); if (v && !isDateInFY(v, selectedFY)) toast.error(getFYDateError(selectedFY, fyLabel)); else reLookupAllHSNRates({...peForm, date: v}, setPeForm); }} required data-testid="pe-date-input" /></div>
              <div className="space-y-1.5"><Label>Invoice #</Label><Input value={peForm.invoice_number} onChange={(e) => setPeForm(p => ({...p, invoice_number: e.target.value}))} placeholder="Optional" data-testid="pe-invoice" /></div>
              <div className="space-y-1.5"><Label>Vehicle #</Label><Input value={peForm.vehicle_number} onChange={(e) => setPeForm(p => ({...p, vehicle_number: e.target.value}))} placeholder="GJ-01-AB-1234" data-testid="pe-vehicle" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3 items-end">
              <div className="space-y-1.5">
                <Label>Credit Days</Label>
                <Input type="number" value={peForm.credit_days} onChange={(e) => setPeForm(p => ({...p, credit_days: e.target.value}))} placeholder="0" data-testid="pe-credit-days" />
              </div>
              {peForm.date && peForm.credit_days && (() => {
                try {
                  const d = new Date(peForm.date);
                  d.setDate(d.getDate() + parseInt(peForm.credit_days));
                  const dueStr = d.toISOString().split('T')[0];
                  const now = new Date(); now.setHours(0,0,0,0);
                  const diff = Math.ceil((d - now) / 86400000);
                  return (
                    <>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Due Date</Label>
                        <p className="text-sm font-mono font-medium h-9 flex items-center">{formatDate(dueStr)}</p>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Remaining</Label>
                        <p className={`text-sm font-semibold h-9 flex items-center ${diff < 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                          {diff < 0 ? `${Math.abs(diff)}d overdue` : `${diff}d remaining`}
                        </p>
                      </div>
                    </>
                  );
                } catch (err) { console.error('Credit days calc failed:', err); return null; }
              })()}
            </div>
            {/* Line Items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Line Items</Label>
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => addLineItem(setPeForm)} data-testid="pe-add-line-btn"><Plus className="h-3 w-3 mr-1" />Item</Button>
              </div>
              {peForm.line_items.map((li, idx) => {
                const prod = products.find(p => p.id === li.product_id);
                const brandOpts = prod?.brands?.filter(b => b)?.map(b => ({ value: b, label: b })) || [];
                const lineAmount = (parseFloat(li.quantity_kg) || 0) * (parseFloat(li.rate) || 0);
                const lineTax = (parseFloat(li.cgst_amount) || 0) + (parseFloat(li.sgst_amount) || 0) + (parseFloat(li.igst_amount) || 0);
                const lineTotal = lineAmount + lineTax;
                const brandSelect = brandOpts.length > 0 ? (
                  <SearchableSelect options={[{value: '', label: 'None'}, ...brandOpts]} value={li.brand} onChange={(v) => updateLineItem(peForm, setPeForm, idx, 'brand', v)} placeholder="Brand" className="h-8 text-xs" data-testid={`pe-line-brand-${idx}`} />
                ) : (
                  <Input value={li.brand} onChange={(e) => updateLineItem(peForm, setPeForm, idx, 'brand', e.target.value)} placeholder="Brand" className="h-8 text-xs" data-testid={`pe-line-brand-${idx}`} />
                );
                return (
                  <div key={idx} className="border rounded-lg px-3 py-2 relative" data-testid={`pe-line-item-${idx}`}>
                    {peForm.line_items.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" className="absolute top-1 right-1 h-5 w-5 text-destructive hover:text-destructive z-10" onClick={() => removeLineItem(setPeForm, idx)} data-testid={`pe-remove-line-${idx}`}><Trash2 className="h-3 w-3" /></Button>
                    )}
                    {/* Desktop */}
                    <div className="hidden sm:block space-y-0.5">
                      <div className="grid grid-cols-[2.2fr_1.5fr_0.8fr_1.2fr_0.6fr_1fr_0.8fr_1fr] gap-2 items-end">
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">Product</Label>}
                          <SearchableSelect options={productOptions} value={li.product_id} onChange={(v) => { updateLineItem(peForm, setPeForm, idx, 'product_id', v); updateLineItem(peForm, setPeForm, idx, 'brand', ''); autoFillLineItemGST(peForm, setPeForm, idx, v); }} placeholder="Product" className="h-8 text-xs" data-testid={`pe-line-product-${idx}`} />
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">Qty (KG)</Label>}
                          <Input type="number" step="0.01" min="0" className="h-8 text-xs" value={li.quantity_kg} onChange={(e) => handleLineItemKgChange(peForm, setPeForm, idx, e.target.value)} data-testid={`pe-line-qty-${idx}`} />
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">Rate</Label>}
                          <Input type="number" step="0.01" min="0" className="h-8 text-xs" value={li.rate} onChange={(e) => { updateLineItem(peForm, setPeForm, idx, 'rate', e.target.value); setTimeout(() => recalcLineItemGST(peForm, setPeForm, idx), 0); }} data-testid={`pe-line-rate-${idx}`} />
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">Brand</Label>}
                          {brandSelect}
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground">GST%</Label>}
                          <Input type="number" step="0.01" min="0" className="h-8 text-xs" value={li.gst_rate} onChange={(e) => { updateLineItem(peForm, setPeForm, idx, 'gst_rate', e.target.value); setTimeout(() => recalcLineItemGST(peForm, setPeForm, idx), 0); }} placeholder="%" data-testid={`pe-line-gst-${idx}`} />
                        </div>
                        <div className="text-right">
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground block">Basic Amt</Label>}
                          <div className="h-8 flex items-center justify-end text-xs font-mono">{lineAmount > 0 ? formatCurrency(lineAmount) : '-'}</div>
                        </div>
                        <div className="text-right">
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground block">Tax</Label>}
                          <div className="h-8 flex items-center justify-end text-xs font-mono text-muted-foreground">{lineTax > 0 ? formatCurrency(lineTax) : '-'}</div>
                        </div>
                        <div className="text-right">
                          {idx === 0 && <Label className="text-[10px] text-muted-foreground block">Total</Label>}
                          <div className="h-8 flex items-center justify-end text-xs font-mono font-semibold">{lineTotal > 0 ? formatCurrency(lineTotal) : '-'}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-[2.2fr_1.5fr_0.8fr_1.2fr_0.6fr_1fr_0.8fr_1fr] gap-2 text-[10px] text-muted-foreground font-mono">
                        <div>{li.hsn_code ? `HSN: ${li.hsn_code}` : ''}</div>
                        <div>{li.quantity_kg ? <><span className="float-right">{toTons(parseFloat(li.quantity_kg))}{li.quantity_bags ? ` / ${li.quantity_bags} Bags` : ''}</span></> : ''}</div>
                        <div></div><div></div><div></div><div></div><div></div><div></div>
                      </div>
                    </div>
                    {/* Mobile */}
                    <div className="sm:hidden space-y-2">
                      <div className="space-y-0.5">
                        <Label className="text-xs">Product</Label>
                        <SearchableSelect options={productOptions} value={li.product_id} onChange={(v) => { updateLineItem(peForm, setPeForm, idx, 'product_id', v); updateLineItem(peForm, setPeForm, idx, 'brand', ''); autoFillLineItemGST(peForm, setPeForm, idx, v); }} placeholder="Select product" data-testid={`pe-line-product-m-${idx}`} />
                        {li.hsn_code && <div className="text-[10px] text-muted-foreground font-mono">HSN: {li.hsn_code}</div>}
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-0.5">
                          <Label className="text-xs">Qty (KG)</Label>
                          <Input type="number" step="0.01" min="0" value={li.quantity_kg} onChange={(e) => handleLineItemKgChange(peForm, setPeForm, idx, e.target.value)} />
                          {li.quantity_kg && <div className="text-[10px] text-muted-foreground font-mono text-right">{toTons(parseFloat(li.quantity_kg))}{li.quantity_bags ? ` / ${li.quantity_bags} B` : ''}</div>}
                        </div>
                        <div className="space-y-0.5"><Label className="text-xs">Rate</Label><Input type="number" step="0.01" min="0" value={li.rate} onChange={(e) => { updateLineItem(peForm, setPeForm, idx, 'rate', e.target.value); setTimeout(() => recalcLineItemGST(peForm, setPeForm, idx), 0); }} /></div>
                        <div className="space-y-0.5"><Label className="text-xs">GST %</Label><Input type="number" step="0.01" min="0" value={li.gst_rate} onChange={(e) => { updateLineItem(peForm, setPeForm, idx, 'gst_rate', e.target.value); setTimeout(() => recalcLineItemGST(peForm, setPeForm, idx), 0); }} /></div>
                      </div>
                      <div className="space-y-0.5"><Label className="text-xs">Brand</Label>{brandSelect}</div>
                      <div className="flex justify-between items-center text-xs pt-1 border-t">
                        <span className="text-muted-foreground">Basic: {lineAmount > 0 ? formatCurrency(lineAmount) : '-'}{lineTax > 0 ? ` + Tax: ${formatCurrency(lineTax)}` : ''}</span>
                        <span className="font-mono font-semibold">{lineTotal > 0 ? formatCurrency(lineTotal) : '-'}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Totals */}
            {(() => {
              const { subtotal, totalTax } = computeTotals(peForm.line_items, 0);
              const rawTotal = subtotal + totalTax;
              const autoRound = rawTotal > 0 ? Math.round(rawTotal) - rawTotal : 0;
              const roundOff = peForm.round_off !== '' ? parseFloat(peForm.round_off) || 0 : Math.round(autoRound * 100) / 100;
              const grand = rawTotal + roundOff;
              return (
                <div className="border-t pt-2 text-sm space-y-0.5">
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono w-28 text-right">{formatCurrency(subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span className="font-mono w-28 text-right">{formatCurrency(totalTax)}</span></div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground flex items-center gap-1">Round Off <Button type="button" variant="ghost" size="icon" className="h-4 w-4" onClick={() => setPeEditRoundOff(!peEditRoundOff)} data-testid="pe-round-off-btn"><Pencil className="h-2.5 w-2.5" /></Button></span>
                    {peEditRoundOff ? (
                      <Input type="number" step="0.01" autoFocus className="w-28 h-7 text-xs text-right font-mono" value={peForm.round_off !== '' ? peForm.round_off : String(Math.round(autoRound * 100) / 100)} onChange={(e) => setPeForm(p => ({...p, round_off: e.target.value}))} onBlur={() => setPeEditRoundOff(false)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setPeEditRoundOff(false); } }} data-testid="pe-round-off-input" />
                    ) : (
                      <span className="font-mono w-28 text-right">{formatCurrency(roundOff)}</span>
                    )}
                  </div>
                  <div className="flex justify-between font-semibold text-base border-t pt-1 mt-1"><span>Grand Total</span><span className="font-mono w-28 text-right">{formatCurrency(grand)}</span></div>
                </div>
              );
            })()}
            <div className="space-y-1.5"><Label>Remarks</Label><Textarea placeholder="Optional..." rows={2} value={peForm.remarks} onChange={(e) => setPeForm(p => ({...p, remarks: e.target.value}))} data-testid="pe-remarks" /></div>
            <DialogFooter>
              <FormShortcutHints shortcuts={[{key: 'Alt+S', label: 'New Supplier'}, {key: '⇧+Enter', label: 'New line'}]} />
              <Button type="button" variant="outline" onClick={() => setPeDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={peSubmitting || !peForm.purchase_order_id || !peForm.supplier_id || !peForm.line_items.some(li => li.product_id)} data-testid="pe-submit-btn">{peSubmitting ? 'Saving...' : editingPE ? 'Update' : 'Add Entry'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* ===== Firm Dialog ===== */}
      <Dialog open={firmDialogOpen} onOpenChange={setFirmDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" onKeyDownCapture={(e) => { if (e.altKey && e.code === 'KeyC') { e.preventDefault(); e.stopPropagation(); const el = document.activeElement; if (el && el.tagName === 'INPUT') el.blur(); setFirmForm(p => ({...p, contact_persons: [...p.contact_persons, {...emptyContact}]})); if (el) setTimeout(() => el.focus(), 0); } }}>
          <DialogHeader><DialogTitle>{editingFirm ? 'Edit Firm' : 'Add Firm'}</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleCreateFirm} className="space-y-4">
            {firmError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{firmError}</AlertDescription></Alert>}
            <div className="space-y-2"><Label>Group</Label>
              <SearchableSelect options={groupOptions} value={firmForm.group_id} onChange={(v) => {
                setFirmForm(p => ({...p, group_id: v}));
                if (!editingFirm) {
                  const existingFirm = allFirms.find(f => f.group_id === v);
                  if (existingFirm && existingFirm.contact_persons?.length > 0) {
                    setFirmForm(p => ({...p, contact_persons: existingFirm.contact_persons.map(cp => ({...cp}))}));
                  }
                }
              }} onCreateNew={(name) => handleCreateGroupFromSelect(name, 'firm')} placeholder="Select or create group" createNewLabel="Add group" data-testid="firm-group-select" />
            </div>
            <div className="space-y-2"><Label>Company Name</Label><Input value={firmForm.company_name} onChange={(e) => setFirmForm(p => ({...p, company_name: e.target.value}))} placeholder="Firm name" required data-testid="firm-name-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={firmForm.company_email} onChange={(e) => setFirmForm(p => ({...p, company_email: e.target.value}))} placeholder="Optional" data-testid="firm-email-input" /></div>
              <div className="space-y-2"><Label>Credit Days</Label><Input type="number" min="0" value={firmForm.credit_days} onChange={(e) => setFirmForm(p => ({...p, credit_days: e.target.value}))} data-testid="firm-credit-input" /></div>
            </div>
            <div className="space-y-2">
              <Label>GSTIN</Label>
              <Input value={firmForm.gstin} onChange={(e) => setFirmForm(p => ({...p, gstin: e.target.value.toUpperCase()}))} placeholder="e.g. 24ABCDE1234F1Z5" maxLength={15} className="font-mono uppercase" data-testid="firm-gstin-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>State</Label>
                <SearchableSelect options={INDIAN_STATES.map(s => ({ value: s.name, label: `${s.code} - ${s.name}` }))} value={firmForm.state} onChange={(v) => { const st = INDIAN_STATES.find(s => s.name === v); setFirmForm(p => ({...p, state: v, state_code: st?.code || ''})); }} placeholder="Select state" data-testid="firm-state-select" />
              </div>
              <div className="space-y-2"><Label>State Code</Label><Input value={firmForm.state_code} readOnly className="bg-muted font-mono" data-testid="firm-state-code" /></div>
            </div>
            <div className="space-y-2">
              <Label>Contact Persons</Label>
              {firmForm.contact_persons.map((cp, idx) => (
                <div key={idx} className="grid grid-cols-2 gap-2 p-2 border rounded">
                  <Input placeholder="Name" value={cp.name} onChange={(e) => { const cps = [...firmForm.contact_persons]; cps[idx] = {...cps[idx], name: e.target.value}; setFirmForm(p => ({...p, contact_persons: cps})); }} />
                  <PhoneInput value={cp.mobile_number} onChange={(e) => { const cps = [...firmForm.contact_persons]; cps[idx] = {...cps[idx], mobile_number: e.target.value}; setFirmForm(p => ({...p, contact_persons: cps})); }} />
                  <Input placeholder="Department" value={cp.department} onChange={(e) => { const cps = [...firmForm.contact_persons]; cps[idx] = {...cps[idx], department: e.target.value}; setFirmForm(p => ({...p, contact_persons: cps})); }} />
                  <Input placeholder="Email" value={cp.email} onChange={(e) => { const cps = [...firmForm.contact_persons]; cps[idx] = {...cps[idx], email: e.target.value}; setFirmForm(p => ({...p, contact_persons: cps})); }} />
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setFirmForm(p => ({...p, contact_persons: [...p.contact_persons, {...emptyContact}]}))}>+ Add Contact <kbd className="ml-1.5 px-1 py-0.5 text-[10px] font-mono bg-muted border rounded">Alt+C</kbd></Button>
            </div>
            <DialogFooter>
              <FormShortcutHints />
              <Button type="button" variant="outline" onClick={() => setFirmDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={firmSubmitting || !firmForm.company_name} data-testid="firm-submit-btn">{firmSubmitting ? 'Saving...' : editingFirm ? 'Update' : 'Add Firm'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* ===== Quick Product Creation Dialog ===== */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New Product</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleCreateProduct} className="space-y-3">
            {productError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{productError}</AlertDescription></Alert>}
            <div className="space-y-2"><Label>Name</Label><Input autoFocus value={productForm.name} onChange={(e) => setProductForm(p => ({...p, name: e.target.value}))} placeholder="e.g. T Gyp" required data-testid="new-product-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Category</Label>
                <SearchableSelect options={[{value: 'raw_material', label: 'Raw Material'}, {value: 'in_process', label: 'In-Process'}, {value: 'finished_goods', label: 'Finished Goods'}]} value={productForm.category} onChange={(v) => setProductForm(p => ({...p, category: v}))} placeholder="Select category" data-testid="new-product-category" />
              </div>
              <div className="space-y-2"><Label>Group</Label>
                <SearchableSelect options={[...new Set(products.map(p => p.group))].filter(Boolean).map(g => ({value: g, label: g}))} value={productForm.group} onChange={(v) => setProductForm(p => ({...p, group: v}))} onCreateNew={(val) => setProductForm(p => ({...p, group: val}))} placeholder="Select or type group" createNewLabel="Add group" data-testid="new-product-group" />
              </div>
            </div>
            <div className="space-y-2"><Label>KG per Bag <span className="text-muted-foreground text-xs">(optional, default 50)</span></Label><Input type="number" step="0.01" min="0" value={productForm.kg_per_bag} onChange={(e) => setProductForm(p => ({...p, kg_per_bag: e.target.value}))} placeholder="e.g. 50" data-testid="new-product-kgperbag" /></div>
            <div className="space-y-2"><Label>Brands <span className="text-muted-foreground text-xs">(comma separated)</span></Label><Input value={productForm.brands} onChange={(e) => setProductForm(p => ({...p, brands: e.target.value}))} placeholder="e.g. Ambuja, Sanstar, Sayaji" data-testid="new-product-brands" /></div>
            <div className="space-y-2"><Label>Description <span className="text-muted-foreground text-xs">(optional)</span></Label><Input value={productForm.description} onChange={(e) => setProductForm(p => ({...p, description: e.target.value}))} placeholder="Product description" data-testid="new-product-description" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>HSN Code</Label>
                <SearchableSelect options={(() => { const seen = new Set(); return hsnCodes.filter(h => { if (seen.has(h.hsn_code)) return false; seen.add(h.hsn_code); return true; }).map(h => ({ value: h.hsn_code, label: `${h.hsn_code} - ${h.description || 'N/A'}` })); })()} value={productForm.hsn_code} onChange={(v) => { const hsn = hsnCodes.filter(h => h.hsn_code === v).sort((a, b) => (b.applicable_from || '').localeCompare(a.applicable_from || ''))[0]; setProductForm(p => ({...p, hsn_code: v, default_gst_rate: hsn ? String(hsn.gst_rate) : p.default_gst_rate})); }} placeholder="Select HSN" data-testid="new-product-hsn" />
              </div>
              <div className="space-y-2"><Label>Default GST Rate %</Label><Input type="number" step="0.01" value={productForm.default_gst_rate} onChange={(e) => setProductForm(p => ({...p, default_gst_rate: e.target.value}))} placeholder="e.g. 18" data-testid="new-product-gst-rate" /></div>
            </div>
            <DialogFooter>
              <FormShortcutHints />
              <Button type="button" variant="outline" onClick={() => setProductDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={productSubmitting || !productForm.name || !productForm.category || !productForm.group} data-testid="new-product-submit">{productSubmitting ? 'Creating...' : 'Create Product'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* ===== Quick Supplier Creation Dialog (from PE form) ===== */}
      <Dialog open={quickFirmDialogOpen} onOpenChange={setQuickFirmDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New Supplier</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleCreateQuickFirm} className="space-y-3">
            {quickFirmError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{quickFirmError}</AlertDescription></Alert>}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Group</Label>
                <SearchableSelect options={groupOptions} value={quickFirmForm.group_id} onChange={handleQuickFirmGroupChange} onCreateNew={(name) => handleCreateGroupFromSelect(name, 'quickfirm')} placeholder="Select group" createNewLabel="Add group" data-testid="quick-firm-group" />
              </div>
              <div className="space-y-1.5"><Label>Company Name</Label><Input autoFocus value={quickFirmForm.company_name} onChange={(e) => setQuickFirmForm(p => ({...p, company_name: e.target.value}))} placeholder="Firm name" required data-testid="quick-firm-name" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={quickFirmForm.company_email} onChange={(e) => setQuickFirmForm(p => ({...p, company_email: e.target.value}))} placeholder="Optional" data-testid="quick-firm-email" /></div>
                <div className="space-y-1.5"><Label>Credit Days</Label><Input type="number" min="0" value={quickFirmForm.credit_days} onChange={(e) => setQuickFirmForm(p => ({...p, credit_days: e.target.value}))} data-testid="quick-firm-credit" /></div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Contact Persons {quickFirmForm.contact_persons.length > 0 && <span className="text-xs text-muted-foreground">(auto-fetched from group)</span>}</Label>
              <div className="space-y-1.5">
                {quickFirmForm.contact_persons.map((cp, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_1fr_0.8fr_1fr_auto] gap-2 items-center">
                    <Input className="h-8 text-sm" placeholder="Name" value={cp.name} onChange={(e) => { const cps = [...quickFirmForm.contact_persons]; cps[idx] = {...cps[idx], name: e.target.value}; setQuickFirmForm(p => ({...p, contact_persons: cps})); }} />
                    <Input className="h-8 text-sm" placeholder="Mobile number" value={cp.mobile_number} onChange={(e) => { const cps = [...quickFirmForm.contact_persons]; cps[idx] = {...cps[idx], mobile_number: e.target.value}; setQuickFirmForm(p => ({...p, contact_persons: cps})); }} />
                    <Input className="h-8 text-sm" placeholder="Dept" value={cp.department} onChange={(e) => { const cps = [...quickFirmForm.contact_persons]; cps[idx] = {...cps[idx], department: e.target.value}; setQuickFirmForm(p => ({...p, contact_persons: cps})); }} />
                    <Input className="h-8 text-sm" placeholder="Email" value={cp.email} onChange={(e) => { const cps = [...quickFirmForm.contact_persons]; cps[idx] = {...cps[idx], email: e.target.value}; setQuickFirmForm(p => ({...p, contact_persons: cps})); }} />
                    <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive" onClick={() => { const cps = quickFirmForm.contact_persons.filter((_, i) => i !== idx); setQuickFirmForm(p => ({...p, contact_persons: cps})); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setQuickFirmForm(p => ({...p, contact_persons: [...p.contact_persons, {name: '', mobile_number: '', department: '', email: ''}]}))}>+ Add Contact</Button>
            </div>
            <DialogFooter>
              <FormShortcutHints />
              <Button type="button" variant="outline" onClick={() => setQuickFirmDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={quickFirmSubmitting || !quickFirmForm.company_name || !quickFirmForm.group_id} data-testid="quick-firm-submit">{quickFirmSubmitting ? 'Creating...' : 'Add Supplier'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>
    </SectionTiles>
    {selectedPayIds.length > 0 && (
      <div className="fixed bottom-14 sm:bottom-14 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-5 py-3 rounded-full shadow-lg border bg-background" data-testid="bulk-action-bar">
        <span className="text-sm font-medium">{selectedPayIds.length} selected</span>
        <Button variant="outline" size="sm" onClick={() => setSelectedPayIds([])} data-testid="clear-selection-btn">Clear</Button>
        <Button variant="destructive" size="sm" onClick={handleBulkDeletePayments} data-testid="bulk-delete-btn"><Trash2 className="h-4 w-4 mr-1" />Delete selected</Button>
      </div>
    )}
    </>
  );
};

export default Purchase;
